import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import Chart from 'chart.js/auto';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import './App2OptiMix.css';
import { AVAILABLE_CITIES_RU, citySlugToRu, cityToSlug, getCitySampleCsv, scaleCityOts } from './cityRouting';
import { authFetch } from './authSession';
import { API_BASE, UPLOAD_API_BASE, CARTO_LIGHT_TILE_URL, CARTO_TILE_ATTRIBUTION } from './config/api';
import CampaignAccessDeniedPage from './CampaignAccessDeniedPage';
import { BannerPopup } from './BannerPopup';
import CopyLinkPage from './CopyLinkPage';
import TrafficMap from './loaded_traffic_map';
import loadingCapybaraGif from './assets/capybara_loading.webp';

const OM_CHART_DAYS_ID = 'om-OhvatiPoChastotamSNacopleniemChart';
const OM_CHART_FREQ_ID = 'om-OhvatSRaznoyChastotoiChart';
const OM_EXPORT_CHART_DAYS_ID = 'om-export-OhvatiPoChastotamSNacopleniemChart';
const OM_EXPORT_CHART_FREQ_ID = 'om-export-OhvatSRaznoyChastotoiChart';

/** Same Chart.js coverage charts as App2.js (days cumulative + frequency). */
function hasDaysChartData(data) {
  const obj = data || {};
  return Object.entries(obj).some(([key, val]) => {
    const num = Number(key);
    if (!(num > 0) || num > 30) return false;
    if (!(num === 1 || num % 5 === 0)) return false;
    return Array.isArray(val) && val.length > 0;
  });
}

function hasFreqChartData(data) {
  const obj = data || {};
  return Object.keys(obj).some((key) => {
    const n = Number(key);
    return Number.isFinite(n) && n > 0 && typeof obj[key] === 'number';
  });
}

function createCoverageChart(canvasId, dataFullDays, x_name_label, y_name_label) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return null;

  const existingChart = Chart.getChart(canvasId);
  if (existingChart) existingChart.destroy();

  const isFreqChart = canvasId === OM_CHART_FREQ_ID || canvasId === OM_EXPORT_CHART_FREQ_ID;
  const hasData = isFreqChart ? hasFreqChartData(dataFullDays) : hasDaysChartData(dataFullDays);
  if (!hasData) return null;

  const ctx = canvas.getContext('2d');
  const originalObject = dataFullDays || {};
  let filteredObject;
  if (isFreqChart) {
    filteredObject = originalObject;
  } else {
    // "Охват/Частота": series by frequency, show only up to frequency 30
    filteredObject = Object.fromEntries(
      Object.entries(originalObject).filter(([key]) => {
        const num = Number(key);
        if (!(num > 0) || num > 30) return false;
        return num === 1 || num % 5 === 0;
      }),
    );
  }

  const valueArrays = Object.values(filteredObject).filter(Array.isArray);
  const maxLength = valueArrays.length
    ? Math.max(...valueArrays.map((arr) => arr.length || 0), 0)
    : 0;
  const freqKeys = isFreqChart
    ? Object.keys(filteredObject)
      .filter((key) => {
        const n = Number(key);
        return Number.isFinite(n) && n > 0 && typeof filteredObject[key] === 'number';
      })
      .sort((a, b) => Number(a) - Number(b))
    : [];
  const labels = isFreqChart
    ? freqKeys
    : Array.from({ length: maxLength }, (_, i) => i + 1);

  const colors = [
    '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
    '#FF9F40', '#C9CBCF', '#E7E9ED', '#FF5733', '#C70039',
    '#900C3F', '#581845', '#2ECC71', '#3498DB', '#E74C3C',
    '#9B59B6', '#1ABC9C', '#F1C40F', '#34495E', '#7F8C8D',
  ];

  const datasets = Object.keys(filteredObject)
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .filter((key) => Array.isArray(filteredObject[key]) && filteredObject[key].length > 0)
    .map((key, index) => {
      const values = filteredObject[key];
      const padded = values.concat(Array(Math.max(0, maxLength - values.length)).fill(null));
      return {
        label: `Частота ${key}`,
        data: padded,
        borderColor: colors[index % colors.length],
        backgroundColor: `${colors[index % colors.length]}80`,
        borderWidth: 2,
        tension: 0.1,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 8,
      };
    });

  return new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: isFreqChart
        ? [{
          label: 'Охват',
          data: freqKeys.map((key) => Number(originalObject[key]) || 0),
          borderColor: '#2563EB',
          backgroundColor: 'rgba(37, 99, 235, 0.12)',
          borderWidth: 2,
          tension: 0.15,
          fill: false,
          pointRadius: 3,
        }]
        : datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: !isFreqChart,
          position: 'top',
        },
        title: { display: false },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: x_name_label,
            font: { size: 12, weight: '600' },
            color: '#64748B',
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: y_name_label,
            font: { size: 12, weight: '600' },
            color: '#64748B',
          },
          grid: { display: true },
        },
      },
    },
  });
}

const ALL_FORMATS_KEY = '__ALL__';
const REACH_N_UI_MAX = 10;
const GOALS = [
  'Оптимизация бюджета',
  'Достичь X% охвата',
];

const TOUR_STEPS = [
  {
    id: 'inventory',
    target: 'inventory',
    title: 'Шаг 1. Загрузите поверхности',
    body: 'Загрузите доступные поверхности в CSV или PDF выборки (all-billboards / Media Catalog). Пример CSV — по ссылке в этом блоке.',
  },
  {
    id: 'goals',
    target: 'goal-options',
    title: 'Шаг 2. Выберите цель кампании',
    body: 'Выберите «оптимизацию бюджета» (максимальный охват при заданном бюджете) или «достижение % охвата». При необходимости включите «Эффективный охват» или «Минимизировать стоимость просмотра». Нажмите «Рассчитать», чтобы получить оптимальную адресную программу.',
    alsoReveal: ['calculate'],
    mergeReveal: true,
  },
  {
    id: 'calculate',
    target: 'map',
    title: 'Шаг 3. Отредактируйте на карте',
    body: 'Ваши поверхности будут отображены на интерактивной карте. При необходимости отредактируйте программу вручную: добавляйте и убирайте поверхности на карте.',
  },
  {
    id: 'analytics',
    target: 'analytics',
    title: 'Шаг 4. Медиаметрики и отчёт',
    body: 'Изучите полученные медиаметрики в сводной аналитике и скачайте отчёт кнопкой «Экспорт».',
    alsoReveal: ['export'],
    mergeReveal: true,
    cardOffset: { left: 36 },
    // Расширяем подсветку вправо (левый край не двигаем).
    spotOffset: { width: 36 },
  },
];

function tourRectsOverlap(a, b, gap = 8) {
  return !(
    a.left + a.width + gap <= b.left
    || b.left + b.width + gap <= a.left
    || a.top + a.height + gap <= b.top
    || b.top + b.height + gap <= a.top
  );
}

/** SVG path for a rounded rect — must match .om-tour-spot border-radius. */
function tourRoundedRectPath(x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));
  if (r <= 0) {
    return `M ${x} ${y} L ${x + w} ${y} L ${x + w} ${y + h} L ${x} ${y + h} Z`;
  }
  return [
    `M ${x + r} ${y}`,
    `L ${x + w - r} ${y}`,
    `A ${r} ${r} 0 0 1 ${x + w} ${y + r}`,
    `L ${x + w} ${y + h - r}`,
    `A ${r} ${r} 0 0 1 ${x + w - r} ${y + h}`,
    `L ${x + r} ${y + h}`,
    `A ${r} ${r} 0 0 1 ${x} ${y + h - r}`,
    `L ${x} ${y + r}`,
    `A ${r} ${r} 0 0 1 ${x + r} ${y}`,
    'Z',
  ].join(' ');
}

const TOUR_SPOT_RADIUS = 12;
const TOUR_SPOT_RADIUS_WHITE = 10;
const TOUR_SPOT_PAD = 8;
const TOUR_SPOT_VIEW_INSET = 10;

/** Keep highlight fully on-screen so the border (esp. bottom) stays visible. */
function fitTourHole(rect, pad = TOUR_SPOT_PAD, inset = TOUR_SPOT_VIEW_INSET) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const top = Math.max(inset, rect.top - pad);
  const left = Math.max(inset, rect.left - pad);
  const right = Math.min(vw - inset, rect.left + rect.width + pad);
  const bottom = Math.min(vh - inset, rect.top + rect.height + pad);
  return {
    top,
    left,
    width: Math.max(24, right - left),
    height: Math.max(24, bottom - top),
  };
}

/** Apply spotOffset after fit — width grows toward the right edge of the screen. */
function applyTourSpotOffset(hole, offset) {
  if (!hole || !offset) return hole;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = hole.left + (offset.left || 0);
  const top = hole.top + (offset.top || 0);
  const maxRight = vw - 4;
  const maxBottom = vh - TOUR_SPOT_VIEW_INSET;
  const width = Math.max(24, Math.min(hole.width + (offset.width || 0), maxRight - left));
  const height = Math.max(24, Math.min(hole.height + (offset.height || 0), maxBottom - top));
  return { ...hole, top, left, width, height };
}

function placeTourCard(hole, cardW, cardH, extras = []) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 16;
  const gap = 14;

  const clamp = (pos) => ({
    top: Math.min(Math.max(margin, pos.top), Math.max(margin, vh - cardH - margin)),
    left: Math.min(Math.max(margin, pos.left), Math.max(margin, vw - cardW - margin)),
  });

  const candidates = [
    { top: hole.top, left: hole.left + hole.width + gap },
    { top: hole.top + hole.height / 2 - cardH / 2, left: hole.left + hole.width + gap },
    { top: hole.top, left: hole.left - gap - cardW },
    { top: hole.top + hole.height / 2 - cardH / 2, left: hole.left - gap - cardW },
    { top: hole.top + hole.height + gap, left: hole.left },
    { top: hole.top + hole.height + gap, left: hole.left + hole.width / 2 - cardW / 2 },
    { top: hole.top - gap - cardH, left: hole.left },
    { top: hole.top - gap - cardH, left: hole.left + hole.width / 2 - cardW / 2 },
    { top: margin, left: vw - cardW - margin },
    { top: vh - cardH - margin, left: vw - cardW - margin },
  ];

  const avoid = [hole, ...extras];
  for (const raw of candidates) {
    const pos = clamp(raw);
    const cardRect = { top: pos.top, left: pos.left, width: cardW, height: cardH };
    if (!avoid.some((r) => tourRectsOverlap(cardRect, r, 6))) {
      return pos;
    }
  }

  let best = clamp({ top: margin, left: vw - cardW - margin });
  let bestScore = Infinity;
  for (const raw of candidates) {
    const pos = clamp(raw);
    const cardRect = { top: pos.top, left: pos.left, width: cardW, height: cardH };
    let score = 0;
    for (const r of avoid) {
      if (!tourRectsOverlap(cardRect, r, 0)) continue;
      const ox = Math.min(cardRect.left + cardRect.width, r.left + r.width) - Math.max(cardRect.left, r.left);
      const oy = Math.min(cardRect.top + cardRect.height, r.top + r.height) - Math.max(cardRect.top, r.top);
      score += Math.max(0, ox) * Math.max(0, oy);
    }
    if (score < bestScore) {
      bestScore = score;
      best = pos;
    }
  }
  return best;
}

function HowItWorksTour({ stepIndex, sampleCsv, onClose, onNext, onPrev }) {
  const step = TOUR_STEPS[stepIndex];
  const cardRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [extraRects, setExtraRects] = useState([]);
  const [cardPos, setCardPos] = useState(null);

  useEffect(() => {
    if (!step) return undefined;

    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (!el) {
        setRect(null);
        setExtraRects([]);
        return;
      }
      el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      const extraNodes = (step.alsoReveal || [])
        .map((id) => document.querySelector(`[data-tour="${id}"]`))
        .filter(Boolean);

      if (step.mergeReveal && extraNodes.length) {
        const nodes = [el, ...extraNodes];
        nodes.forEach((node) => {
          node.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        });
        let top = Infinity;
        let left = Infinity;
        let right = -Infinity;
        let bottom = -Infinity;
        nodes.forEach((node) => {
          const b = node.getBoundingClientRect();
          top = Math.min(top, b.top);
          left = Math.min(left, b.left);
          right = Math.max(right, b.right);
          bottom = Math.max(bottom, b.bottom);
        });
        setRect({
          top,
          left,
          width: right - left,
          height: bottom - top,
        });
        setExtraRects([]);
        return;
      }

      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
      const extras = extraNodes.map((node) => {
        const b = node.getBoundingClientRect();
        return { top: b.top, left: b.left, width: b.width, height: b.height };
      });
      setExtraRects(extras);
    };

    measure();
    const t = window.setTimeout(measure, 280);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step, stepIndex]);

  useEffect(() => {
    if (!rect) {
      setCardPos(null);
      return undefined;
    }
    const hole = applyTourSpotOffset(
      { ...fitTourHole(rect), radius: TOUR_SPOT_RADIUS },
      step.spotOffset,
    );
    const place = () => {
      const cardEl = cardRef.current;
      const cardW = cardEl ? cardEl.offsetWidth : 340;
      const cardH = cardEl ? cardEl.offsetHeight : 180;
      const extras = extraRects.map((r) => ({
        ...fitTourHole(r, 4, TOUR_SPOT_VIEW_INSET),
        radius: TOUR_SPOT_RADIUS_WHITE,
      }));
      const pos = placeTourCard(hole, cardW, cardH, extras);
      const ox = step.cardOffset?.left || 0;
      const oy = step.cardOffset?.top || 0;
      if (!ox && !oy) {
        setCardPos(pos);
        return;
      }
      const margin = 16;
      setCardPos({
        top: Math.min(
          Math.max(margin, pos.top + oy),
          Math.max(margin, window.innerHeight - cardH - margin),
        ),
        left: Math.min(
          Math.max(margin, pos.left + ox),
          Math.max(margin, window.innerWidth - cardW - margin),
        ),
      });
    };
    place();
    const t = window.setTimeout(place, 50);
    return () => window.clearTimeout(t);
  }, [rect, extraRects, stepIndex, sampleCsv, step]);

  if (!step) return null;

  const hole = rect
    ? applyTourSpotOffset(
      { ...fitTourHole(rect), radius: TOUR_SPOT_RADIUS },
      step.spotOffset,
    )
    : null;

  const extraHoles = extraRects.map((r) => ({
    ...fitTourHole(r, 4, TOUR_SPOT_VIEW_INSET),
    radius: TOUR_SPOT_RADIUS_WHITE,
  }));

  const allHoles = hole ? [hole, ...extraHoles] : extraHoles;
  const shadeClipPath = (() => {
    if (!allHoles.length) return undefined;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const outer = `M 0 0 L ${vw} 0 L ${vw} ${vh} L 0 ${vh} Z`;
    const inners = allHoles
      .map((h) => tourRoundedRectPath(h.left, h.top, h.width, h.height, h.radius ?? TOUR_SPOT_RADIUS))
      .join(' ');
    return `path(evenodd, "${outer} ${inners}")`;
  })();

  const tooltipStyle = cardPos
    ? { top: cardPos.top, left: cardPos.left }
    : hole
      ? { top: Math.min(hole.top, window.innerHeight - 200), left: Math.min(hole.left + hole.width + 14, window.innerWidth - 360) }
      : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="om-tour" role="dialog" aria-modal="true" aria-label="Как это работает">
      <div
        className="om-tour-shade"
        style={shadeClipPath ? { clipPath: shadeClipPath } : undefined}
        onClick={onClose}
      />
      {hole && (
        <div
          className="om-tour-spot"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
          }}
        />
      )}
      {extraHoles.map((r, i) => (
        <div
          key={i}
          className="om-tour-spot om-tour-spot--white"
          style={{
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
          }}
        />
      ))}
      <div className="om-tour-card" ref={cardRef} style={tooltipStyle}>
        <div className="om-tour-card-top">
          <span className="om-tour-step">{stepIndex + 1} / {TOUR_STEPS.length}</span>
          <button type="button" className="om-tour-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
        {step.id === 'inventory' && sampleCsv && (
          <a className="om-tour-link" href={sampleCsv.href} download={sampleCsv.download}>
            Скачать пример файла
          </a>
        )}
        <div className="om-tour-actions">
          <button type="button" className="om-btn" onClick={onPrev} disabled={stepIndex === 0}>
            Назад
          </button>
          {stepIndex < TOUR_STEPS.length - 1 ? (
            <button type="button" className="om-btn om-btn-primary" onClick={onNext}>
              Далее
            </button>
          ) : (
            <button type="button" className="om-btn om-btn-primary" onClick={onClose}>
              Готово
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const EMPTY_STATS = {
  lengthBanners: 0,
  ots: 0,
  frequency: 0,
  coverage: 0,
  totalBudget: 0,
  cpt: 0,
  real_perc_cover: 0,
};

function normalizeBannerState(value) {
  return Array.isArray(value) ? value : [];
}
function normalizeArray(value) {
  return Array.isArray(value) ? value : [];
}
function normalizeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}
function normalizeObject(value, fallback = {}) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  return fallback;
}
function normalizeFormatValue(value) {
  return String(value || '').trim();
}

/** Stable payload used for autosave dedupe (must match POST body shape). */
function buildBannerStateSavePayload({
  handChooseBanners,
  tranzitStateAddedBnrs,
  tranzitStateDelBnrs,
  delBanners,
  lenSelectedBanners,
  coverCityCost,
  coverCityPerc,
  savedCountMoney,
  o_t_s,
  u_s,
  dataFullDays,
  dataKasaniya,
  currentSum,
  savedPercentage,
  selectedGoal,
  savedWithMoney,
  useReachNPlus,
  reachN,
  stats,
  inventoryJsonPath,
  inventoryOriginalFilename,
  inventorySnapshotId,
  daysAutomatedIntegratedPath,
  trafficMapPath,
  availableFormats,
  uploadedBnrs,
  NotTouchesBnrs,
  a_curve_parameter,
  max_banners,
  a_curve_max_reach,
  reachNCurves,
}) {
  return {
    handChooseBanners: normalizeBannerState(handChooseBanners),
    tranzitStateAddedBnrs: normalizeBannerState(tranzitStateAddedBnrs),
    tranzitStateDelBnrs: normalizeBannerState(tranzitStateDelBnrs),
    cityCalculationState: {
      delBanners: normalizeArray(delBanners),
      lenSelectedBanners: normalizeNumber(lenSelectedBanners),
      coverCityCost: normalizeNumber(coverCityCost),
      coverCityPerc: normalizeNumber(coverCityPerc),
      countMoney: savedCountMoney,
      o_t_s: normalizeNumber(o_t_s),
      u_s: normalizeNumber(u_s),
      dataFullDays: normalizeObject(dataFullDays),
      data_kasaniya: normalizeObject(dataKasaniya),
      currentSum: normalizeNumber(currentSum),
      percentage: savedPercentage,
      selectedMethod: normalizeNumber(selectedGoal, 0),
      withMoney: Boolean(savedWithMoney),
      useReachNPlus: Boolean(useReachNPlus),
      reachN: Math.min(REACH_N_UI_MAX, Math.max(1, normalizeNumber(reachN, 1))),
      stats: normalizeObject(stats, {}),
    },
    inventoryJsonPath: inventoryJsonPath || '',
    inventoryOriginalFilename: inventoryOriginalFilename || '',
    inventorySnapshotId: inventorySnapshotId || '',
    daysAutomatedIntegratedPath: daysAutomatedIntegratedPath || '',
    trafficMapPath: trafficMapPath || '',
    availableFormats: normalizeArray(availableFormats).map(normalizeFormatValue).filter(Boolean),
    loadedBannersCount: normalizeNumber(uploadedBnrs, 0),
    notHitBannersCount: normalizeNumber(NotTouchesBnrs, 0),
    reach_five_plus: normalizeNumber(dataKasaniya?.['5'], 0),
    reach_n_curves: normalizeObject(reachNCurves, {}),
    a_curve_parameter,
    max_banners,
    a_curve_max_reach,
  };
}

function buildBannerStateSavePayloadFromApi(data) {
  const calc = normalizeObject(data?.cityCalculationState, {});
  let selectedGoal = normalizeNumber(calc?.selectedMethod, 0);
  let useReachNPlus = Boolean(calc?.useReachNPlus);
  // legacy: goal index 2 was standalone Reach N+ — fold into mode 0
  if (selectedGoal === 2) {
    selectedGoal = 0;
    useReachNPlus = true;
  } else {
    selectedGoal = selectedGoal === 1 ? 1 : 0;
  }
  return buildBannerStateSavePayload({
    handChooseBanners: data?.handChooseBanners,
    tranzitStateAddedBnrs: data?.tranzitStateAddedBnrs,
    tranzitStateDelBnrs: data?.tranzitStateDelBnrs,
    delBanners: calc?.delBanners,
    lenSelectedBanners: calc?.lenSelectedBanners,
    coverCityCost: calc?.coverCityCost,
    coverCityPerc: calc?.coverCityPerc,
    savedCountMoney: calc?.countMoney ?? '',
    o_t_s: calc?.o_t_s,
    u_s: calc?.u_s,
    dataFullDays: calc?.dataFullDays,
    dataKasaniya: calc?.data_kasaniya,
    currentSum: calc?.currentSum,
    savedPercentage: calc?.percentage ?? '',
    selectedGoal,
    savedWithMoney: calc?.withMoney,
    useReachNPlus,
    reachN: calc?.reachN,
    stats: { ...EMPTY_STATS, ...normalizeObject(calc?.stats, {}) },
    inventoryJsonPath: typeof data?.inventoryJsonPath === 'string' ? data.inventoryJsonPath : '',
    inventoryOriginalFilename: typeof data?.inventoryOriginalFilename === 'string' ? data.inventoryOriginalFilename : '',
    inventorySnapshotId: typeof data?.inventorySnapshotId === 'string' ? data.inventorySnapshotId : '',
    daysAutomatedIntegratedPath: typeof data?.daysAutomatedIntegratedPath === 'string' ? data.daysAutomatedIntegratedPath : '',
    trafficMapPath: typeof data?.trafficMapPath === 'string' ? data.trafficMapPath : '',
    availableFormats: data?.availableFormats,
    uploadedBnrs: data?.loadedBannersCount,
    NotTouchesBnrs: data?.notHitBannersCount,
    a_curve_parameter: normalizeNumber(data?.a_curve_parameter, 0),
    max_banners: normalizeNumber(data?.max_banners, 0),
    a_curve_max_reach: normalizeNumber(data?.a_curve_max_reach, 100),
    reachNCurves: data?.reach_n_curves,
  });
}

function bannerStateSaveKey(payload) {
  try {
    return JSON.stringify(payload);
  } catch {
    return '';
  }
}

function getBannerFormat(banner) {
  if (!banner || typeof banner !== 'object') return '';
  return normalizeFormatValue(banner['Формат'] ?? banner.format ?? banner.bannerFormat);
}
function getUniqueFormatsFromBanners(bannersList) {
  if (!Array.isArray(bannersList)) return [];
  const unique = new Set();
  for (const banner of bannersList) {
    const fmt = getBannerFormat(banner);
    if (fmt) unique.add(fmt);
  }
  return Array.from(unique);
}
function getUserIdFromToken(token) {
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) return '';
    const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(normalized));
    return payload?.sub || '';
  } catch {
    return '';
  }
}
function formatRuDate(value) {
  if (!value) return '—';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}
function pluralBanners(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'билборд';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'билборда';
  return 'билбордов';
}
function parseCoords(banner) {
  const raw = banner?.coordinates || banner?.coords || '';
  if (typeof raw === 'string' && raw.includes(',')) {
    const [lat, lon] = raw.split(',').map(Number);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return [lat, lon];
  }
  const lat = Number(banner?.lat ?? banner?.Широта);
  const lon = Number(banner?.lon ?? banner?.Долгота);
  if (Number.isFinite(lat) && Number.isFinite(lon)) return [lat, lon];
  return null;
}
function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('ru-RU').format(Math.round(n));
}

function BrandMark() {
  return (
    <svg className="om-brand-mark" width="24" height="21" viewBox="0 0 24 21" fill="none" aria-hidden>
      <path
        d="M12.8252 0.00553429C13.5238 0.00466141 14.2681 -0.00864774 14.9629 0.00944054L16.3281 1.35612C17.1603 1.40348 18.2089 1.37365 19.0596 1.3737L23.9785 1.37565L23.9766 4.94206C23.9778 5.8302 24.1586 6.97551 23.4893 7.63444C23.2976 7.82423 23.0604 7.96164 22.8008 8.03385C22.3898 8.14532 20.5827 8.09134 20.0332 8.10026L20.042 13.6149C19.0755 14.4848 17.9843 15.3355 17.0127 16.2087C16.6798 16.5079 16.0904 16.9461 15.8027 17.235C15.7804 18.1798 15.7912 19.1929 15.7988 20.1413C14.4341 20.1442 13.0334 20.1256 11.6738 20.1462C11.6744 19.2108 11.6564 18.2089 11.6807 17.279L5.28516 17.277C5.06517 18.1646 4.80853 19.2789 4.5459 20.1423H0C0.234441 19.011 0.43135 17.7061 0.616211 16.5612L1.29395 12.4294L1.77734 9.49772C1.84715 9.0903 1.99566 8.38845 2.03711 8.01042C2.15221 7.94336 2.88744 7.9723 3.08008 7.97233H5.0957C6.79086 7.97211 8.61717 7.93918 10.3027 7.98014C10.8741 7.36907 11.4878 6.7869 12.0742 6.1901C12.2984 5.96199 12.6176 5.67045 12.8242 5.43717C12.83 3.64114 12.8449 1.80052 12.8252 0.00553429ZM19.9941 14.4958C20.0663 14.5668 20.041 15.7394 20.041 15.9118L20.043 18.8512C20.0417 19.1121 20.0171 19.9191 20.0537 20.1432H16.8789C16.7575 20.1477 16.6823 20.1563 16.5645 20.1198C16.4902 19.8305 16.5485 17.868 16.5488 17.4479C16.997 17.0301 17.5495 16.5891 18.0234 16.1911C18.6872 15.634 19.3441 15.0688 19.9941 14.4958ZM8.87988 17.9049C8.85453 18.6081 8.87522 19.4291 8.87793 20.1413L8.28516 20.1432L5.22754 20.1393C5.4198 19.5931 5.66498 18.5256 5.83789 17.9079L8.87988 17.9049ZM18.2441 3.12663C17.6785 3.12759 17.0846 3.13804 16.5215 3.12858L16.5195 4.27702L18.2432 4.27311L18.2441 3.12663Z"
        fill="#3B5EE9"
      />
    </svg>
  );
}

function IconUndo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M6.5 3.5 3 7l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.2 7H10a3 3 0 0 1 0 6H8.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconRedo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M9.5 3.5 13 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.8 7H6a3 3 0 0 0 0 6h1.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconChevronDown({ open = false }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
      style={{
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 160ms ease',
      }}
    >
      <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M2.5 7.2 5.6 10.2 11.5 3.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CitySelect({ value, options, onChange, onAddCity }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const list = options?.length ? options : (value ? [value] : []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`om-city-select${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="om-btn om-select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="om-select-label">{value || 'Город'}</span>
        <IconChevronDown open={open} />
      </button>

      {open && (
        <div className="om-city-menu" role="listbox" aria-label="Город">
          <div className="om-city-menu-title">Города кампании</div>
          <ul className="om-city-list">
            {list.map((city) => {
              const active = city === value;
              return (
                <li key={city}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`om-city-option${active ? ' is-active' : ''}`}
                    onClick={() => {
                      setOpen(false);
                      if (city !== value) onChange?.(city);
                    }}
                  >
                    <span className="om-city-option-dot" aria-hidden />
                    <span className="om-city-option-text">{city}</span>
                    {active && (
                      <span className="om-city-option-check">
                        <IconCheck />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            className="om-city-add-btn"
            onClick={() => {
              setOpen(false);
              onAddCity?.();
            }}
          >
            <IconPlus />
            Добавить город
          </button>
        </div>
      )}
    </div>
  );
}
function IconChevronLeft() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M4.5 2.5 8 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconEye() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M1.5 8s2.5-4 6.5-4 6.5 4 6.5 4-2.5 4-6.5 4-6.5-4-6.5-4Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="1.8" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
function IconPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M7 2.5v9M2.5 7h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconMinus() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M2.5 7h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconExpand() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M2.5 5V2.5H5M9 2.5h2.5V5M11.5 9v2.5H9M5 11.5H2.5V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconCompress() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M5 2.5V5H2.5M11.5 5H9V2.5M9 11.5V9h2.5M2.5 9H5v2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconTraffic() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M3.5 11.5v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8 11.5V5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12.5 11.5V3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function IconMapBanner() {
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden>
      <rect x="1" y="1" width="12" height="8" rx="1.2" fill="currentColor" />
      <path d="M4 11h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconUpload() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
      <path d="M14 18.5V6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9.5 11 14 6.5 18.5 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 21.5h17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function IconFile() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 2.5h5.5L12.5 5.5V13.5H4V2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 2.5V5.5H12.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

const selectedIcon = L.divIcon({
  className: 'om-marker-icon om-marker-selected',
  html: `<div class="om-marker-hit om-marker-hit--selected">
    <div class="om-marker-core om-marker-core--selected">
      <svg width="14" height="12" viewBox="0 0 14 12" fill="none"><rect x="1" y="1" width="12" height="8" rx="1.2" fill="#fff"/><path d="M4 11h6" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/></svg>
    </div>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});
const dotIcon = L.divIcon({
  className: 'om-marker-icon om-marker-dot',
  html: `<div class="om-marker-hit om-marker-hit--dot">
    <div class="om-marker-core om-marker-core--dot"></div>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -10],
});
const deletedIcon = L.divIcon({
  className: 'om-marker-icon om-marker-deleted',
  html: `<div class="om-marker-hit om-marker-hit--deleted">
    <div class="om-marker-core om-marker-core--deleted"></div>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -10],
});

function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.setView(center, zoom ?? map.getZoom(), { animate: false });
  }, [center, zoom, map]);
  return null;
}

function MapBridge({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    return () => {
      mapRef.current = null;
    };
  }, [map, mapRef]);
  return null;
}

/** Access gate — same rules as CampaignShareRoute */
function withCampaignAccess(Wrapped) {
  return function Guarded(props) {
    const { publicId } = useParams();
    const location = useLocation();
    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
      let cancelled = false;
      const run = async () => {
        if (!publicId) {
          setStatus('not_found');
          return;
        }
        setStatus('loading');
        const token = localStorage.getItem('authToken');
        try {
          const response = await authFetch(`${API_BASE}/campaigns/${publicId}/access`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const data = await response.json();
          if (cancelled) return;
          if (response.status === 401 || data?.reason === 'auth_required') {
            setStatus('auth_required');
            return;
          }
          if (response.status === 403 || data?.reason === 'forbidden') {
            setStatus('forbidden');
            return;
          }
          if (response.status === 404) {
            setStatus('not_found');
            return;
          }
          if (!response.ok) throw new Error(data?.message || 'Ошибка доступа');
          setStatus(data?.hasAccess ? 'allowed' : 'forbidden');
        } catch (e) {
          if (!cancelled) {
            setErrorMessage(e.message || 'Ошибка доступа');
            setStatus('error');
          }
        }
      };
      run();
      return () => {
        cancelled = true;
      };
    }, [publicId]);

    if (status === 'loading') {
      return <div className="om-gate">Проверяем доступ к кампании…</div>;
    }
    if (status === 'auth_required') {
      return <Navigate to={`/auth?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />;
    }
    if (status === 'forbidden') return <CampaignAccessDeniedPage />;
    if (status === 'not_found') return <div className="om-gate">Кампания не найдена.</div>;
    if (status === 'error') return <div className="om-gate om-gate-error">{errorMessage}</div>;
    return <Wrapped {...props} />;
  };
}

function App2OptiMixScreen() {
  const navigate = useNavigate();
  const { publicId, citySlug } = useParams();
  const cityName = citySlugToRu(citySlug);
  const sampleCsv = getCitySampleCsv(cityName || citySlug);
  const mapRef = useRef(null);
  const trafficMapRef = useRef(null);
  const fileInputRef = useRef(null);
  const metricsPollRef = useRef(null);
  const suppressBannerStateSaveRef = useRef(true);
  const lastSavedBannerStateKeyRef = useRef('');
  const suppressBannerStateSaveTimerRef = useRef(null);
  const adoptLiveBannerStateKeyRef = useRef(false);
  const [bannerStateSaveGate, setBannerStateSaveGate] = useState(0);

  const armBannerStateSaveSuppress = useCallback((durationMs = 1500) => {
    suppressBannerStateSaveRef.current = true;
    if (suppressBannerStateSaveTimerRef.current) {
      clearTimeout(suppressBannerStateSaveTimerRef.current);
    }
    suppressBannerStateSaveTimerRef.current = setTimeout(() => {
      suppressBannerStateSaveRef.current = false;
      suppressBannerStateSaveTimerRef.current = null;
      // Re-run autosave effect after suppress window (e.g. edits during undo/load).
      setBannerStateSaveGate((v) => v + 1);
    }, durationMs);
  }, []);

  useEffect(() => () => {
    if (suppressBannerStateSaveTimerRef.current) {
      clearTimeout(suppressBannerStateSaveTimerRef.current);
    }
  }, []);

  const [campaign, setCampaign] = useState(null);
  const [campaignCities, setCampaignCities] = useState([]);
  const [cityPopulation, setCityPopulation] = useState(0);
  const [campaignError, setCampaignError] = useState('');
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(true);

  const [selectedGoal, setSelectedGoal] = useState(0);
  const [savedSelectedGoal, setSavedSelectedGoal] = useState(0);
  const [useReachNPlus, setUseReachNPlus] = useState(false);
  const [reachN, setReachN] = useState('1');
  const [savedUseReachNPlus, setSavedUseReachNPlus] = useState(false);
  const [savedReachN, setSavedReachN] = useState('1');
  const [budgetInput, setBudgetInput] = useState('');
  const [percentage, setPercentage] = useState('');
  const [withMoney, setWithMoney] = useState(false);
  const [savedWithMoney, setSavedWithMoney] = useState(false);
  const [savedCountMoney, setSavedCountMoney] = useState('');
  const [savedPercentage, setSavedPercentage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [banners, setBanners] = useState([]);
  const [HandChooseBanners, setHandChooseBanners] = useState([]);
  const [TranzitStateAddedBnrs, setTranzitStateAddedBnrs] = useState([]);
  const [TranzitStateDelBnrs, setTranzitStateDelBnrs] = useState([]);
  const [delBanners, setDelBanners] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState(ALL_FORMATS_KEY);
  const [availableFormats, setAvailableFormats] = useState([]);

  const [inventoryJsonPath, setInventoryJsonPath] = useState('');
  const [inventoryOriginalFilename, setInventoryOriginalFilename] = useState('');
  const [inventorySnapshotId, setInventorySnapshotId] = useState('');
  const [daysAutomatedIntegratedPath, setDaysAutomatedIntegratedPath] = useState('');
  const [trafficMapPath, setTrafficMapPath] = useState('');
  const [uploadedBnrs, setUploadedBnrs] = useState(0);
  const [NotTouchesBnrs, setNotTouchesBnrs] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [metricsStatus, setMetricsStatus] = useState('');
  const [metricsPhase, setMetricsPhase] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [o_t_s, setOtS] = useState(0);
  const [u_s, setUs] = useState(0);
  const [coverCityPerc, setCoverCityPerc] = useState(0);
  const [coverCityCost, setCoverCityCost] = useState(0);
  const [countMoney, setCountMoney] = useState('');
  const [lenSelectedBanners, setLenSelectedBanners] = useState(0);
  const [dataFullDays, setDataFullDays] = useState({});
  const [dataKasaniya, setDataKasaniya] = useState({});
  const [currentSum, setCurrentSum] = useState(0);
  const [stats, setStats] = useState({ ...EMPTY_STATS });
  const [a_curve_parameter, set_a_curve_parameter] = useState(0);
  const [max_banners, set_max_banners] = useState(0);
  const [a_curve_max_reach, set_a_curve_max_reach] = useState(100);
  const [reachNCurves, setReachNCurves] = useState({});
  const [CurStateId, setCurStateId] = useState(null);
  const [isBannerStateLoaded, setIsBannerStateLoaded] = useState(false);
  const [bannerStateHistory, setBannerStateHistory] = useState([]);
  const [bannerHistoryCursor, setBannerHistoryCursor] = useState(-1);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [showSurfaces, setShowSurfaces] = useState(true);
  const [isChartCollapsed, setIsChartCollapsed] = useState(false);
  const [choosedGraphicBottom, setChoosedGraphicBottom] = useState('first_graphic');
  const [goalCollapsed, setGoalCollapsed] = useState(false);
  const [analyticsCollapsed, setAnalyticsCollapsed] = useState(false);
  const [tourStep, setTourStep] = useState(null);
  const [copyLinkModal, setCopyLinkModal] = useState(false);
  const [showAddCityModal, setShowAddCityModal] = useState(false);
  const [selectedCitiesToAdd, setSelectedCitiesToAdd] = useState([]);
  const [isAddingCity, setIsAddingCity] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [activeMapType, setActiveMapType] = useState('default');
  const [trafficMapWarm, setTrafficMapWarm] = useState(false);
  const [choosedBanner, setChoosedBanner] = useState('');
  const [mapCenter, setMapCenter] = useState([47.22486, 39.70229]);
  const [zoomLevel, setZoomLevel] = useState(11);

  useEffect(() => {
    if (activeMapType === 'traffic') setTrafficMapWarm(true);
  }, [activeMapType]);

  useEffect(() => {
    if (!trafficMapPath) setTrafficMapWarm(false);
  }, [trafficMapPath]);

  const metricsBlockCalculations = metricsStatus === 'processing';

  const cityCenter = useMemo(() => {
    const found = AVAILABLE_CITIES_RU.find((c) => c[0] === cityName);
    return found ? found[1] : [47.22486, 39.70229];
  }, [cityName]);

  useEffect(() => {
    setMapCenter(cityCenter);
  }, [cityCenter]);

  const stopMetricsPolling = useCallback(() => {
    if (metricsPollRef.current) {
      clearInterval(metricsPollRef.current);
      metricsPollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopMetricsPolling(), [stopMetricsPolling]);

  const fetchBanners = useCallback(async (jsonPath) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/get_all_banners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ PathToBanners: jsonPath || '' }),
      });
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setBanners(list);
      const formats = getUniqueFormatsFromBanners(list);
      if (formats.length) setAvailableFormats(formats);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  }, []);

  const applyMetricsFromStatus = useCallback((data) => {
    if (!data) return false;
    setMetricsPhase(data.phase || '');
    if (data.status === 'ready') {
      setUploading(false);
      setMetricsStatus('ready');
      set_a_curve_parameter(data.a_curve_parameter || 0);
      set_max_banners(data.max_banners || 0);
      if (data.a_curve_max_reach != null) set_a_curve_max_reach(Number(data.a_curve_max_reach) || 100);
      if (data.reach_n_curves && typeof data.reach_n_curves === 'object') {
        setReachNCurves(data.reach_n_curves);
      }
      if (data.ads_loaded != null) setUploadedBnrs(data.ads_loaded);
      if (data.ads_not_hit != null) setNotTouchesBnrs(data.ads_not_hit);
      if (typeof data.traffic_map_path === 'string') setTrafficMapPath(data.traffic_map_path);
      stopMetricsPolling();
      return true;
    }
    if (data.status === 'failed') {
      setUploading(false);
      setMetricsStatus('failed');
      if (typeof data.error === 'string' && data.error.trim()) {
        setUploadError(`Не удалось посчитать метрики: ${data.error.trim()}`);
      }
      stopMetricsPolling();
      return false;
    }
    setUploading(true);
    setMetricsStatus('processing');
    return false;
  }, [stopMetricsPolling]);

  const fetchMetricsStatus = useCallback(async (daysPath, jsonPath = null) => {
    if (!daysPath) return null;
    try {
      const response = await fetch(
        `${UPLOAD_API_BASE}/api/inventory-status?days_path=${encodeURIComponent(daysPath)}`,
      );
      if (!response.ok) return null;
      const data = await response.json();
      const ready = applyMetricsFromStatus(data);
      if (ready && jsonPath) await fetchBanners(jsonPath);
      return data;
    } catch (error) {
      console.error('Ошибка статуса метрик:', error);
      return null;
    }
  }, [applyMetricsFromStatus, fetchBanners]);

  const startMetricsPolling = useCallback((daysPath, jsonPath = null) => {
    stopMetricsPolling();
    setMetricsStatus('processing');
    fetchMetricsStatus(daysPath, jsonPath);
    metricsPollRef.current = setInterval(() => {
      fetchMetricsStatus(daysPath, jsonPath);
    }, 3000);
  }, [fetchMetricsStatus, stopMetricsPolling]);

  const resetScreenStateForNewInventory = () => {
    setHandChooseBanners([]);
    setTranzitStateAddedBnrs([]);
    setTranzitStateDelBnrs([]);
    setDelBanners([]);
    setLenSelectedBanners(0);
    setCoverCityCost(0);
    setCoverCityPerc(0);
    setBudgetInput('');
    setCountMoney('');
    setSavedCountMoney('');
    setOtS(0);
    setUs(0);
    setDataFullDays({});
    setDataKasaniya({});
    setCurrentSum(0);
    setPercentage('');
    setSavedPercentage('');
    setWithMoney(false);
    setSavedWithMoney(false);
    setSelectedGoal(0);
    setSavedSelectedGoal(0);
    setUseReachNPlus(false);
    setSavedUseReachNPlus(false);
    setReachN('1');
    setSavedReachN('1');
    setStats({ ...EMPTY_STATS });
    setTrafficMapPath('');
    setActiveMapType('default');
    setChoosedBanner('');
  };

  const applyBannerSnapshot = useCallback((data) => {
    setHandChooseBanners(normalizeBannerState(data?.handChooseBanners));
    setTranzitStateAddedBnrs(normalizeBannerState(data?.tranzitStateAddedBnrs));
    setTranzitStateDelBnrs(normalizeBannerState(data?.tranzitStateDelBnrs));
    const calc = normalizeObject(data?.cityCalculationState, {});
    setInventoryJsonPath(typeof data?.inventoryJsonPath === 'string' ? data.inventoryJsonPath : '');
    setInventoryOriginalFilename(typeof data?.inventoryOriginalFilename === 'string' ? data.inventoryOriginalFilename : '');
    setInventorySnapshotId(typeof data?.inventorySnapshotId === 'string' ? data.inventorySnapshotId : '');
    setDaysAutomatedIntegratedPath(typeof data?.daysAutomatedIntegratedPath === 'string' ? data.daysAutomatedIntegratedPath : '');
    setTrafficMapPath(typeof data?.trafficMapPath === 'string' ? data.trafficMapPath : '');
    setAvailableFormats(normalizeArray(data?.availableFormats).map(normalizeFormatValue).filter(Boolean));
    setSelectedFormat(ALL_FORMATS_KEY);
    setUploadedBnrs(normalizeNumber(data?.loadedBannersCount, 0));
    setNotTouchesBnrs(normalizeNumber(data?.notHitBannersCount, 0));
    setDelBanners(normalizeArray(calc?.delBanners));
    setLenSelectedBanners(normalizeNumber(calc?.lenSelectedBanners));
    setCoverCityCost(normalizeNumber(calc?.coverCityCost));
    setCoverCityPerc(normalizeNumber(calc?.coverCityPerc));
    setBudgetInput(calc?.countMoney ?? '');
    setSavedCountMoney(calc?.countMoney ?? '');
    setCountMoney(normalizeObject(calc?.stats, {})?.totalBudget ?? calc?.countMoney ?? '');
    setOtS(normalizeNumber(calc?.o_t_s));
    setUs(normalizeNumber(calc?.u_s));
    setDataFullDays(normalizeObject(calc?.dataFullDays));
    setDataKasaniya(normalizeObject(calc?.data_kasaniya));
    setCurrentSum(normalizeNumber(calc?.currentSum));
    setPercentage(calc?.percentage ?? '');
    setSavedPercentage(calc?.percentage ?? '');
    {
      const method = normalizeNumber(calc?.selectedMethod, 0);
      // legacy: goal index 2 was standalone Reach N+ — fold into mode 0
      let nextUseReachNPlus = Boolean(calc?.useReachNPlus);
      let nextGoal = method === 1 ? 1 : 0;
      if (method === 2) {
        nextGoal = 0;
        nextUseReachNPlus = true;
      }
      setSelectedGoal(nextGoal);
      setSavedSelectedGoal(nextGoal);
      setUseReachNPlus(nextUseReachNPlus);
      setSavedUseReachNPlus(nextUseReachNPlus);
      const n = Math.min(REACH_N_UI_MAX, Math.max(1, normalizeNumber(calc?.reachN, 1)));
      setReachN(String(n));
      setSavedReachN(String(n));
    }
    setWithMoney(Boolean(calc?.withMoney));
    setSavedWithMoney(Boolean(calc?.withMoney));
    setStats({ ...EMPTY_STATS, ...normalizeObject(calc?.stats, {}) });
    set_a_curve_parameter(normalizeNumber(data?.a_curve_parameter, 0));
    set_max_banners(normalizeNumber(data?.max_banners, 0));
    set_a_curve_max_reach(normalizeNumber(data?.a_curve_max_reach, 100));
    setReachNCurves(normalizeObject(data?.reach_n_curves, {}));
    const daysPath = typeof data?.daysAutomatedIntegratedPath === 'string' ? data.daysAutomatedIntegratedPath : '';
    const jsonPath = typeof data?.inventoryJsonPath === 'string' ? data.inventoryJsonPath : '';
    if (daysPath) {
      fetchMetricsStatus(daysPath, jsonPath).then((statusData) => {
        if (statusData?.status === 'processing') startMetricsPolling(daysPath, jsonPath);
      });
    } else {
      setMetricsStatus(jsonPath ? 'ready' : '');
    }
    return jsonPath;
  }, [fetchMetricsStatus, startMetricsPolling]);

  const loadBannerStateHistory = useCallback(async (activeStateId = null) => {
    if (!publicId || !cityName) return;
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch(
        `${API_BASE}/campaigns/${publicId}/cities/${encodeURIComponent(cityName)}/banners-state/history`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) return;
      const data = await response.json();
      const states = normalizeArray(data?.states);
      setBannerStateHistory(states);
      if (!states.length) {
        setBannerHistoryCursor(-1);
        setCurStateId(null);
        return;
      }
      if (activeStateId) {
        const foundIndex = states.findIndex((item) => item?.id === activeStateId);
        const nextIndex = foundIndex >= 0 ? foundIndex : states.length - 1;
        setBannerHistoryCursor(nextIndex);
        setCurStateId(states[nextIndex]?.id || null);
      } else {
        setBannerHistoryCursor(states.length - 1);
        setCurStateId(states[states.length - 1]?.id || null);
      }
    } catch {
      /* no-op */
    }
  }, [publicId, cityName]);

  const applyHistoryStateById = async (stateId, nextCursor) => {
    if (!publicId || !cityName || !stateId) return;
    setIsHistoryLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch(
        `${API_BASE}/campaigns/${publicId}/cities/${encodeURIComponent(cityName)}/banners-state/history/${stateId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) return;
      const data = await response.json();
      // Undo/redo only navigates history — never append a new DB snapshot.
      armBannerStateSaveSuppress(1500);
      adoptLiveBannerStateKeyRef.current = true;
      lastSavedBannerStateKeyRef.current = bannerStateSaveKey(buildBannerStateSavePayloadFromApi(data));
      applyBannerSnapshot(data);
      await fetchBanners(typeof data?.inventoryJsonPath === 'string' ? data.inventoryJsonPath : '');
      setBannerHistoryCursor(nextCursor);
      setCurStateId(stateId);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // Load campaign
  useEffect(() => {
    if (!publicId || !citySlug) {
      navigate('/campaigns', { replace: true });
      return;
    }
    const population = AVAILABLE_CITIES_RU.find((c) => c[0] === cityName)?.[2] || 0;
    setCityPopulation(population);

    let cancelled = false;
    const loadCampaign = async () => {
      setIsLoadingCampaign(true);
      setCampaignError('');
      try {
        const token = localStorage.getItem('authToken');
        const response = await authFetch(`${API_BASE}/campaigns/${publicId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.message || 'Не удалось загрузить кампанию');
        if (cancelled) return;
        setCampaign(data);
        const loadedCities = data.cityNames || [];
        setCampaignCities(loadedCities);
        const normalizedCurrentCity = cityName || loadedCities[0];
        if (!normalizedCurrentCity) {
          navigate('/campaigns', { replace: true });
          return;
        }
        if (!loadedCities.includes(normalizedCurrentCity)) {
          navigate(`/${publicId}/${cityToSlug(loadedCities[0])}`, { replace: true });
          return;
        }
        const canonicalSlug = cityToSlug(normalizedCurrentCity);
        if (canonicalSlug && canonicalSlug !== citySlug) {
          navigate(`/${publicId}/${canonicalSlug}`, { replace: true });
        }
      } catch (error) {
        if (!cancelled) setCampaignError(error.message || 'Ошибка загрузки кампании');
      } finally {
        if (!cancelled) setIsLoadingCampaign(false);
      }
    };
    loadCampaign();
    return () => {
      cancelled = true;
    };
  }, [publicId, cityName, citySlug, navigate]);

  // Load city banner state
  useEffect(() => {
    if (!publicId || !cityName) {
      setIsBannerStateLoaded(false);
      return;
    }
    let cancelled = false;
    armBannerStateSaveSuppress(1500);
    setIsBannerStateLoaded(false);
    setStats({ ...EMPTY_STATS });

    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await authFetch(
          `${API_BASE}/campaigns/${publicId}/cities/${encodeURIComponent(cityName)}/banners-state`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!response.ok) throw new Error('Cannot load city banner state');
        const data = await response.json();
        if (cancelled) return;
        setCurStateId(data?.activeStateId || null);
        adoptLiveBannerStateKeyRef.current = true;
        lastSavedBannerStateKeyRef.current = bannerStateSaveKey(buildBannerStateSavePayloadFromApi(data));
        applyBannerSnapshot(data);
        await loadBannerStateHistory(data?.activeStateId || null);
        await fetchBanners(typeof data?.inventoryJsonPath === 'string' ? data.inventoryJsonPath : '');
        // Recover traffic map path from inventory snapshot status if not stored in city state yet.
        const storedTrafficPath = typeof data?.trafficMapPath === 'string' ? data.trafficMapPath.trim() : '';
        const daysPath = typeof data?.daysAutomatedIntegratedPath === 'string'
          ? data.daysAutomatedIntegratedPath.trim()
          : '';
        if (!storedTrafficPath && daysPath) {
          const status = await fetchMetricsStatus(daysPath);
          if (!cancelled && typeof status?.traffic_map_path === 'string' && status.traffic_map_path) {
            setTrafficMapPath(status.traffic_map_path);
          }
        }
      } catch {
        if (!cancelled) {
          setHandChooseBanners([]);
          setTranzitStateAddedBnrs([]);
          setTranzitStateDelBnrs([]);
          setStats({ ...EMPTY_STATS });
        }
      } finally {
        if (!cancelled) setIsBannerStateLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [publicId, cityName, applyBannerSnapshot, fetchBanners, fetchMetricsStatus, loadBannerStateHistory, armBannerStateSaveSuppress]);

  // Autosave banner state
  useEffect(() => {
    if (!isBannerStateLoaded || !publicId || !cityName) return;
    if (suppressBannerStateSaveRef.current || isHistoryLoading) return;
    if (isProcessing || uploading || metricsBlockCalculations) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const currentUserId = getUserIdFromToken(token);
    if (campaign?.userId && currentUserId && currentUserId !== campaign.userId) return;

    const payload = buildBannerStateSavePayload({
      handChooseBanners: HandChooseBanners,
      tranzitStateAddedBnrs: TranzitStateAddedBnrs,
      tranzitStateDelBnrs: TranzitStateDelBnrs,
      delBanners,
      lenSelectedBanners,
      coverCityCost,
      coverCityPerc,
      savedCountMoney,
      o_t_s,
      u_s,
      dataFullDays,
      dataKasaniya,
      currentSum,
      savedPercentage,
      selectedGoal: savedSelectedGoal,
      savedWithMoney,
      useReachNPlus: savedUseReachNPlus,
      reachN: savedReachN,
      stats,
      inventoryJsonPath,
      inventoryOriginalFilename,
      inventorySnapshotId,
      daysAutomatedIntegratedPath,
      trafficMapPath,
      availableFormats,
      uploadedBnrs,
      NotTouchesBnrs,
      a_curve_parameter,
      max_banners,
      a_curve_max_reach,
      reachNCurves,
    });
    const nextKey = bannerStateSaveKey(payload);

    // After undo/load, adopt the live fingerprint so normalization drift doesn't POST a clone.
    if (adoptLiveBannerStateKeyRef.current) {
      adoptLiveBannerStateKeyRef.current = false;
      lastSavedBannerStateKeyRef.current = nextKey;
      return;
    }

    if (nextKey && nextKey === lastSavedBannerStateKeyRef.current) return;

    const timeoutId = setTimeout(async () => {
      try {
        if (suppressBannerStateSaveRef.current || isHistoryLoading) return;
        if (nextKey && nextKey === lastSavedBannerStateKeyRef.current) return;
        // Claim before await to prevent concurrent duplicate POSTs.
        lastSavedBannerStateKeyRef.current = nextKey;
        const response = await authFetch(
          `${API_BASE}/campaigns/${publicId}/cities/${encodeURIComponent(cityName)}/banners-state`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          },
        );
        if (response.ok) {
          const result = await response.json();
          setCurStateId(result?.stateId || null);
          if (!result?.deduped) {
            await loadBannerStateHistory(result?.stateId || null);
          }
        } else if (nextKey && lastSavedBannerStateKeyRef.current === nextKey) {
          lastSavedBannerStateKeyRef.current = '';
        }
      } catch {
        if (nextKey && lastSavedBannerStateKeyRef.current === nextKey) {
          lastSavedBannerStateKeyRef.current = '';
        }
      }
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [
    isBannerStateLoaded, publicId, cityName, campaign?.userId,
    HandChooseBanners, TranzitStateAddedBnrs, TranzitStateDelBnrs,
    delBanners, lenSelectedBanners, coverCityCost, coverCityPerc,
    savedCountMoney, o_t_s, u_s, dataFullDays, dataKasaniya, currentSum,
    savedPercentage, savedWithMoney, savedSelectedGoal, savedUseReachNPlus, savedReachN, stats,
    inventoryJsonPath, inventoryOriginalFilename, inventorySnapshotId,
    daysAutomatedIntegratedPath, trafficMapPath, availableFormats,
    uploadedBnrs, NotTouchesBnrs, a_curve_parameter, max_banners, a_curve_max_reach, reachNCurves,
    isProcessing, uploading, metricsBlockCalculations, isHistoryLoading, loadBannerStateHistory,
    bannerStateSaveGate,
  ]);

  const load_csv_data = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    setMetricsStatus('processing');

    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('authToken') || '';
    const currentUserId = getUserIdFromToken(token);
    if (currentUserId) formData.append('user_id', currentUserId);
    if (publicId) formData.append('public_id', publicId);
    if (cityName) formData.append('city_name', cityName);

    try {
      const response = await fetch(`${UPLOAD_API_BASE}/api/upload-csv`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Ошибка сервера');
      }
      const result = await response.json();
      setInventoryJsonPath(result?.processed_json_abs_path || '');
      setInventoryOriginalFilename(result?.original_filename || file?.name || '');
      setInventorySnapshotId(result?.inventory_snapshot_id || '');
      setDaysAutomatedIntegratedPath(result?.days_out_dir_abs_path || '');
      setTrafficMapPath(result?.traffic_map_path || '');
      const formatsFromCsv = normalizeArray(result?.availableFormats).map(normalizeFormatValue).filter(Boolean);
      setAvailableFormats(formatsFromCsv);
      setSelectedFormat(ALL_FORMATS_KEY);
      resetScreenStateForNewInventory();
      set_a_curve_parameter(0);
      set_max_banners(0);
      set_a_curve_max_reach(100);
      setReachNCurves({});
      setUploadedBnrs(0);
      setNotTouchesBnrs(0);
      await fetchBanners(result?.processed_json_abs_path || '');
      if (result?.days_out_dir_abs_path) {
        startMetricsPolling(result.days_out_dir_abs_path, result.processed_json_abs_path || '');
      } else {
        setUploading(false);
        setMetricsStatus('ready');
      }
    } catch (error) {
      console.error(error);
      setUploadError(error.message || 'Ошибка загрузки');
      setUploading(false);
      setMetricsStatus('failed');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) load_csv_data(file);
    if (e.target) e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) load_csv_data(file);
  };

  const parseGetBannersResponse = async (response) => {
    if (response.status === 409) {
      const err = await response.json();
      if (err.error === 'METRICS_NOT_READY') {
        setMetricsStatus('processing');
      }
      return null;
    }
    if (response.status === 429) {
      alert('Предыдущий расчёт ещё выполняется. Подождите 1–2 минуты.');
      return null;
    }
    if (!response.ok) {
      let message = `Ошибка расчёта (${response.status})`;
      try {
        const err = await response.json();
        if (err?.message) message = err.message;
      } catch { /* ignore */ }
      alert(message);
      return null;
    }
    return response.json();
  };

  const readData = async (inputValue, path, event) => {
    if (metricsBlockCalculations) return null;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE}/get_banners?${event === 'cost' ? 'cost=' : 'percent='}${inputValue}&path=${path}&cheepeutiful=${withMoney ? 'cheep' : 'beatiful'}&del_banners={}&choosed_banners=${TranzitStateDelBnrs.length > 0 ? TranzitStateDelBnrs.map((b) => b.id) : ''}`,
        {
          method: 'POST',
          body: JSON.stringify({
            daysAutomatedIntegratedPath: daysAutomatedIntegratedPath + '/',
            publicId,
            cityName,
            stateId: CurStateId,
            inventoryJsonPath: inventoryJsonPath || '',
            max_banners: max_banners || undefined,
            a_curve_parameter: a_curve_parameter || undefined,
            a_curve_max_reach: a_curve_max_reach || undefined,
            reach_n_curves: reachNCurves,
            useReachNPlus: Boolean(useReachNPlus),
            reachN: Math.min(REACH_N_UI_MAX, Math.max(1, Number(reachN) || 1)),
          }),
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const data = await parseGetBannersResponse(response);
      if (!data) return null;
      setLenSelectedBanners(Object.keys(data.selected_banners || {}).length);
      setCoverCityPerc(data.real_perc_cover);
      setCountMoney(data.count_money);
      setOtS(data.o_t_s);
      setUs(data.u_s);
      setDataFullDays(data.dataFullDays);
      setDataKasaniya(data.data_kasania);
      setCurrentSum(data.currentSum);
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const sendChoosedBanners = async (added_bnr, option) => {
    if (isProcessing || metricsBlockCalculations) {
      if (metricsBlockCalculations) alert('Метрики ещё считаются, подождите…');
      return;
    }
    setIsProcessing(true);
    try {
      let ChoosedBnrs = [];
      if (option === 'add') ChoosedBnrs = [...HandChooseBanners, added_bnr];
      if (option === 'del') ChoosedBnrs = HandChooseBanners.filter((item) => item.id !== added_bnr.id);

      if (!ChoosedBnrs.length) {
        setHandChooseBanners([]);
        setTranzitStateAddedBnrs([]);
        setLenSelectedBanners(0);
        setCoverCityCost(0);
        setCoverCityPerc(0);
        setBudgetInput('');
        setCountMoney('');
        setSavedCountMoney('');
        setOtS(0);
        setUs(0);
        setDataFullDays({});
        setDataKasaniya({});
        setCurrentSum(0);
        setPercentage('');
        setSavedPercentage('');
        setUseReachNPlus(false);
        setSavedUseReachNPlus(false);
        setReachN('1');
        setSavedReachN('1');
        setStats({ ...EMPTY_STATS });
        return;
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE}/get_banners?cheepeutiful=count_with_only_my_bnrs&del_banners={}&choosed_banners=${ChoosedBnrs.map((b) => b.id)}`,
        {
          method: 'POST',
          body: JSON.stringify({
            daysAutomatedIntegratedPath,
            publicId,
            cityName,
            stateId: CurStateId,
            useReachNPlus: Boolean(useReachNPlus),
            reachN: Math.min(REACH_N_UI_MAX, Math.max(1, Number(reachN) || 1)),
            reach_n_curves: reachNCurves,
          }),
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const data = await parseGetBannersResponse(response);
      if (!data) return;

      const selectedBanners = data.selected_banners || {};
      const array_brns = Object.values(selectedBanners);
      setHandChooseBanners(array_brns);
      const count = array_brns.length;
      setStats({
        ...EMPTY_STATS,
        lengthBanners: count,
        ots: data.o_t_s || 0,
        frequency: (data.u_s || 0).toFixed(2),
        totalBudget: data.count_money,
        cpt: data.o_t_s ? ((data.count_money / data.o_t_s) * 1000).toFixed(2) : 0,
        real_perc_cover: data.real_perc_cover,
      });
      setLenSelectedBanners(count);
      setCoverCityPerc(data.real_perc_cover);
      setCountMoney(data.count_money);
      setOtS(data.o_t_s);
      setUs(data.u_s);
      setDataFullDays(data.dataFullDays);
      setDataKasaniya(data.data_kasania);
      setCurrentSum(data.currentSum);
      // Persist goal inputs only after metrics came back from backend.
      setSavedWithMoney(withMoney);
      setSavedUseReachNPlus(Boolean(useReachNPlus));
      setSavedReachN(String(Math.min(REACH_N_UI_MAX, Math.max(1, normalizeNumber(reachN, 1)))));
    } catch (error) {
      console.error('Ошибка при выборе/удалении баннера:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const createBannerAction = (isSelected, markerId) => async () => {
    if (isProcessing || metricsBlockCalculations) {
      if (metricsBlockCalculations) alert('Метрики ещё считаются, подождите…');
      return;
    }
    const need = banners.find((b) => b.id === markerId);
    if (!need) return;
    if (isSelected) {
      setHandChooseBanners((prev) => prev.filter((item) => item.id !== need.id));
      setTranzitStateAddedBnrs((prev) => prev.filter((item) => item.id !== need.id));
      await sendChoosedBanners(need, 'del');
    } else {
      // If user re-selects a previously excluded banner, return it to calculations.
      setTranzitStateDelBnrs((prev) => prev.filter((item) => item.id !== need.id));
      setTranzitStateAddedBnrs((prev) => [...prev, need]);
      setHandChooseBanners((prev) => [...prev, need]);
      await sendChoosedBanners(need, 'add');
    }
  };

  const createBannerDelete = (markerId) => async () => {
    if (isProcessing || metricsBlockCalculations) return;
    const need = banners.find((b) => b.id === markerId);
    if (!need) return;
    const wasSelected = HandChooseBanners.some((item) => item.id === need.id);
    setHandChooseBanners((prev) => prev.filter((item) => item.id !== need.id));
    setTranzitStateAddedBnrs((prev) => prev.filter((item) => item.id !== need.id));
    setTranzitStateDelBnrs((prev) => (
      prev.some((item) => item.id === need.id) ? prev : [...prev, need]
    ));
    if (wasSelected) {
      await sendChoosedBanners(need, 'del');
    }
  };

  const createBannerRestore = (markerId) => () => {
    if (isProcessing || metricsBlockCalculations) return;
    setTranzitStateDelBnrs((prev) => prev.filter((item) => item.id !== markerId));
  };

  const processInput = async (event) => {
    if (isProcessing || metricsBlockCalculations) {
      if (metricsBlockCalculations) alert('Метрики ещё считаются, подождите…');
      return;
    }
    setIsProcessing(true);
    try {
      let path = 'folder_for_few_attempts/return_older_not_uniq_car_counts_traffic_lights.json';
      if (event !== 'cost') {
        if (!percentage || String(percentage).trim() === '') {
          alert('Введите процент охвата');
          setIsProcessing(false);
          return;
        }
      } else if (!budgetInput || String(budgetInput).trim() === '') {
        alert('Введите бюджет');
        setIsProcessing(false);
        return;
      }
      const inputValue = event === 'cost' ? budgetInput : percentage;
      const readDataAnswer = await readData(Number(inputValue), path, event);
      const selectedBanners = readDataAnswer?.selected_banners;
      if (!readDataAnswer || !selectedBanners || Object.keys(selectedBanners).length === 0) {
        setIsProcessing(false);
        return;
      }
      const array_brns = Object.values(selectedBanners);
      setHandChooseBanners(array_brns);
      const count = array_brns.length;
      setStats({
        ...EMPTY_STATS,
        lengthBanners: count,
        ots: readDataAnswer.o_t_s || 0,
        frequency: (readDataAnswer.u_s || 0).toFixed(2),
        totalBudget: readDataAnswer.count_money,
        cpt: ((readDataAnswer.count_money / readDataAnswer.o_t_s) * 1000).toFixed(2),
        real_perc_cover: readDataAnswer.real_perc_cover,
      });
      setSavedWithMoney(withMoney);
      setSavedUseReachNPlus(Boolean(useReachNPlus));
      setSavedReachN(String(Math.min(REACH_N_UI_MAX, Math.max(1, normalizeNumber(reachN, 1)))));
      setSavedSelectedGoal(selectedGoal === 1 ? 1 : 0);
      if (event === 'cost') setSavedCountMoney(inputValue);
      else setSavedPercentage(inputValue);
      setDelBanners([]);
      setTranzitStateAddedBnrs([]);
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const activeBanners = useMemo(() => {
    if (!Array.isArray(banners) || !banners.length) return [];
    if (selectedFormat === ALL_FORMATS_KEY) return banners;
    return banners.filter((b) => getBannerFormat(b) === selectedFormat);
  }, [banners, selectedFormat]);

  const selectedIdSet = useMemo(
    () => new Set(HandChooseBanners.map((b) => b.id)),
    [HandChooseBanners],
  );
  const deletedIdSet = useMemo(
    () => new Set(TranzitStateDelBnrs.map((b) => b.id)),
    [TranzitStateDelBnrs],
  );

  const mapMarkers = useMemo(() => {
    const items = [];
    for (const banner of activeBanners) {
      const pos = parseCoords(banner);
      if (!pos) continue;
      const selected = selectedIdSet.has(banner.id);
      const deleted = deletedIdSet.has(banner.id);
      items.push({
        id: banner.id,
        position: pos,
        selected,
        deleted,
        photo: banner['Фото'] || banner.photo || '',
        map: banner['Карта'] || banner.map || '',
        price: banner.price,
        address: banner['Адрес'] || banner.address || '',
      });
    }
    return items;
  }, [activeBanners, selectedIdSet, deletedIdSet]);

  const derivedKpi = useMemo(() => {
    const totalBudget = Number(stats?.totalBudget) || 0;
    const frequency = Number(stats?.frequency) || 0;
    const reachPercent = Number(stats?.real_perc_cover) || 0;
    const grp = reachPercent > 0 && frequency > 0
      ? Number((reachPercent * frequency).toFixed(2))
      : 0;
    const fleetSize = Number(process.env.REACT_APP_UNIQUE_CARS_DENOM) || 30000;
    const reachedUsers = (reachPercent / 100) * fleetSize;
    const cpu = reachedUsers > 0 ? Number((totalBudget / reachedUsers).toFixed(2)) : 0;
    return { grp, cpu };
  }, [stats]);

  const otsFinal = useMemo(
    () => scaleCityOts(o_t_s || stats.ots, cityPopulation),
    [o_t_s, stats.ots, cityPopulation],
  );
  const cptFinal = useMemo(() => {
    const money = Number(countMoney || stats.totalBudget) || 0;
    if (!(otsFinal > 0) || !(money > 0)) return stats.cpt || '—';
    return ((money / otsFinal) * 1000).toFixed(2);
  }, [countMoney, stats.totalBudget, otsFinal, stats.cpt]);

  const reachFivePlus = useMemo(
    () => normalizeNumber(dataKasaniya?.['5'], 0),
    [dataKasaniya],
  );

  useEffect(() => {
    if (isChartCollapsed) return undefined;
    const t = setTimeout(() => {
      Chart.getChart(OM_CHART_DAYS_ID)?.destroy();
      Chart.getChart(OM_CHART_FREQ_ID)?.destroy();
      if (choosedGraphicBottom === 'first_graphic') {
        const daysCanvas = document.getElementById(OM_CHART_DAYS_ID);
        if (daysCanvas) {
          createCoverageChart(OM_CHART_DAYS_ID, dataFullDays, 'Дни', 'Охват, %');
        }
      } else {
        const freqCanvas = document.getElementById(OM_CHART_FREQ_ID);
        if (freqCanvas) {
          createCoverageChart(OM_CHART_FREQ_ID, dataKasaniya, 'Частота, ед', 'Охват, %');
        }
      }
    }, 100);
    return () => {
      clearTimeout(t);
      Chart.getChart(OM_CHART_DAYS_ID)?.destroy();
      Chart.getChart(OM_CHART_FREQ_ID)?.destroy();
    };
  }, [dataFullDays, dataKasaniya, choosedGraphicBottom, isChartCollapsed]);

  useEffect(() => {
    if (!showExportModal) return undefined;
    const t = setTimeout(() => {
      Chart.getChart(OM_EXPORT_CHART_DAYS_ID)?.destroy();
      Chart.getChart(OM_EXPORT_CHART_FREQ_ID)?.destroy();
      if (document.getElementById(OM_EXPORT_CHART_DAYS_ID)) {
        createCoverageChart(OM_EXPORT_CHART_DAYS_ID, dataFullDays, 'Дни', 'Охват, %');
      }
      if (document.getElementById(OM_EXPORT_CHART_FREQ_ID)) {
        createCoverageChart(OM_EXPORT_CHART_FREQ_ID, dataKasaniya, 'Частота, ед', 'Охват, %');
      }
    }, 100);
    return () => {
      clearTimeout(t);
      Chart.getChart(OM_EXPORT_CHART_DAYS_ID)?.destroy();
      Chart.getChart(OM_EXPORT_CHART_FREQ_ID)?.destroy();
    };
  }, [showExportModal, dataFullDays, dataKasaniya]);

  useEffect(() => {
    const t = setTimeout(() => {
      mapRef.current?.invalidateSize?.(true);
    }, 240);
    return () => clearTimeout(t);
  }, [goalCollapsed, analyticsCollapsed, isChartCollapsed, isMapFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const mapWrap = document.querySelector('.om-map-wrap');
      const active = Boolean(document.fullscreenElement && document.fullscreenElement === mapWrap);
      setIsMapFullscreen(active);
      setTimeout(() => mapRef.current?.invalidateSize?.(true), 120);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const metricsPhaseLabel = (() => {
    switch (metricsPhase) {
      case 'kasanie': return 'Считаем касания баннеров с дорогами…';
      case 'days': return 'Генерируем данные по дням…';
      case 'filtering': return 'Фильтруем баннеры по касаниям…';
      case 'three_dots': return 'Строим модель охвата…';
      case 'done': return 'Готово';
      default: return 'Считаем метрики по баннерам…';
    }
  })();

  const canUndo = bannerHistoryCursor > 0;
  const canRedo = bannerHistoryCursor >= 0 && bannerHistoryCursor < bannerStateHistory.length - 1;

  const availableCitiesToAdd = useMemo(
    () => AVAILABLE_CITIES_RU.filter((city) => !campaignCities.includes(city[0])),
    [campaignCities],
  );

  const handleAddCities = async () => {
    if (selectedCitiesToAdd.length === 0) return;
    setIsAddingCity(true);
    try {
      const token = localStorage.getItem('authToken');
      for (const city of selectedCitiesToAdd) {
        const response = await authFetch(`${API_BASE}/campaigns/${publicId}/cities`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ cityName: city }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.message || `Не удалось добавить город: ${city}`);
        }
      }
      setCampaignCities((prev) => [...prev, ...selectedCitiesToAdd]);
      setShowAddCityModal(false);
      setSelectedCitiesToAdd([]);
    } catch (error) {
      console.error('Ошибка при добавлении города:', error);
      alert(error.message || 'Ошибка при добавлении города');
    } finally {
      setIsAddingCity(false);
    }
  };

  const formatTags = useMemo(() => {
    const tags = [{ key: ALL_FORMATS_KEY, label: 'Все' }];
    for (const f of availableFormats) tags.push({ key: f, label: f });
    return tags;
  }, [availableFormats]);

  const setMapType = (nextType) => {
    if (nextType === activeMapType) return;
    const source = activeMapType === 'traffic' ? trafficMapRef.current : mapRef.current;
    if (source) {
      const c = source.getCenter();
      const z = source.getZoom();
      if (c) setMapCenter([c.lat, c.lng]);
      if (Number.isFinite(z)) setZoomLevel(z);
    }
    setActiveMapType(nextType);
  };

  const toggleMapType = () => {
    setMapType(activeMapType === 'traffic' ? 'default' : 'traffic');
  };

  const coverPeople = Math.round((cityPopulation / 1000) * (Number(coverCityPerc) || Number(stats.real_perc_cover) || 0) / 100);

  if (isLoadingCampaign) {
    return <div className="om-gate">Загружаем кампанию…</div>;
  }
  if (campaignError) {
    return <div className="om-gate om-gate-error">{campaignError}</div>;
  }

  return (
    <div className="om-root">
      <header className="om-top">
        <div className="om-top-left">
          <a className="om-brand" href="#brand" onClick={(e) => e.preventDefault()}>
            <BrandMark />
            <span>Купи.баннер</span>
          </a>
          <div className="om-campaign-meta">
            <span>{campaign?.name || 'Кампания'}</span>
            <span className="om-sep" />
            <span>
              {formatRuDate(campaign?.startDate)} – {formatRuDate(campaign?.endDate)}
            </span>
          </div>
        </div>

        <div className="om-top-actions">
          <button
            type="button"
            className="om-btn om-btn-ghost-tour"
            title="Как это работает?"
            onClick={() => {
              setGoalCollapsed(false);
              setAnalyticsCollapsed(false);
              setTourStep(0);
            }}
          >
            <span className="om-tour-label-full">Как это работает?</span>
            <span className="om-tour-label-short">Как работает?</span>
          </button>
          <button
            type="button"
            className="om-icon-btn"
            title="Отменить"
            disabled={!canUndo || isHistoryLoading}
            onClick={() => {
              if (!canUndo || isHistoryLoading) return;
              const next = bannerHistoryCursor - 1;
              applyHistoryStateById(bannerStateHistory[next]?.id, next);
            }}
          >
            <IconUndo />
          </button>
          <button
            type="button"
            className="om-icon-btn"
            title="Вернуть"
            disabled={!canRedo || isHistoryLoading}
            onClick={() => {
              if (!canRedo || isHistoryLoading) return;
              const next = bannerHistoryCursor + 1;
              applyHistoryStateById(bannerStateHistory[next]?.id, next);
            }}
          >
            <IconRedo />
          </button>

          <CitySelect
            value={cityName}
            options={campaignCities.length ? campaignCities : [cityName]}
            onAddCity={() => {
              setSelectedCitiesToAdd([]);
              setShowAddCityModal(true);
            }}
            onChange={(next) => {
              if (next) navigate(`/${publicId}/${cityToSlug(next)}`);
            }}
          />

          <button type="button" className="om-btn" onClick={() => navigate('/campaigns')}>
            К кампаниям
          </button>
          <button
            type="button"
            className="om-btn"
            onClick={() => setCopyLinkModal(true)}
          >
            Скопировать ссылку
          </button>
          <button
            type="button"
            className="om-btn om-btn-primary"
            data-tour="export"
            onClick={() => setShowExportModal(true)}
          >
            Экспорт
          </button>
        </div>
      </header>

      <CopyLinkPage
        isOpen={copyLinkModal}
        onClose={() => setCopyLinkModal(false)}
        publicId={publicId}
      />

      {tourStep != null && (
        <HowItWorksTour
          stepIndex={tourStep}
          sampleCsv={sampleCsv}
          onClose={() => setTourStep(null)}
          onNext={() => setTourStep((s) => Math.min(TOUR_STEPS.length - 1, (s ?? 0) + 1))}
          onPrev={() => setTourStep((s) => Math.max(0, (s ?? 0) - 1))}
        />
      )}

      {showAddCityModal && (
        <div
          className="om-add-city-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Добавить город"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isAddingCity) {
              setShowAddCityModal(false);
              setSelectedCitiesToAdd([]);
            }
          }}
        >
          <div className="om-add-city-card">
            <div className="om-add-city-head">
              <div>
                <h2 className="om-add-city-title">Добавить город</h2>
                <p className="om-add-city-subtitle">Выберите города для добавления в кампанию</p>
              </div>
              <button
                type="button"
                className="om-add-city-close"
                disabled={isAddingCity}
                onClick={() => {
                  setShowAddCityModal(false);
                  setSelectedCitiesToAdd([]);
                }}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="om-add-city-list">
              {availableCitiesToAdd.length === 0 ? (
                <div className="om-add-city-empty">Все доступные города уже добавлены</div>
              ) : (
                availableCitiesToAdd.map((city) => {
                  const name = city[0];
                  const checked = selectedCitiesToAdd.includes(name);
                  return (
                    <label key={name} className={`om-add-city-item${checked ? ' is-checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCitiesToAdd((prev) => [...prev, name]);
                          } else {
                            setSelectedCitiesToAdd((prev) => prev.filter((c) => c !== name));
                          }
                        }}
                      />
                      <span>{name}</span>
                    </label>
                  );
                })
              )}
            </div>

            <div className="om-add-city-foot">
              <button
                type="button"
                className="om-btn"
                disabled={isAddingCity}
                onClick={() => {
                  setShowAddCityModal(false);
                  setSelectedCitiesToAdd([]);
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className="om-btn om-btn-primary"
                disabled={selectedCitiesToAdd.length === 0 || isAddingCity}
                onClick={handleAddCities}
              >
                {isAddingCity ? 'Добавление...' : 'Добавить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <div
          className="om-export-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Экспорт отчёта"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowExportModal(false);
          }}
        >
          <div className="om-export-card" id="om-export-print-root">
            <div className="om-export-toolbar no-print">
              <button
                type="button"
                className="om-btn om-btn-primary"
                onClick={() => {
                  const prevTitle = document.title;
                  const safeCity = String(cityName || 'city').replace(/[\\/:*?"<>|]+/g, '_');
                  document.title = `OptiMix_${safeCity}_${publicId || 'report'}`;
                  const modal = document.querySelector('.om-export-modal');
                  if (modal) modal.scrollTop = 0;
                  window.scrollTo(0, 0);
                  document.documentElement.classList.add('om-printing');
                  let restored = false;
                  const restore = () => {
                    if (restored) return;
                    restored = true;
                    document.documentElement.classList.remove('om-printing');
                    document.title = prevTitle;
                    window.removeEventListener('afterprint', restore);
                  };
                  window.addEventListener('afterprint', restore);
                  // Let layout expand (remove viewport clipping) before print
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      window.print();
                      setTimeout(restore, 2000);
                    });
                  });
                }}
              >
                Скачать PDF
              </button>
              <button
                type="button"
                className="om-export-close"
                aria-label="Закрыть"
                onClick={() => setShowExportModal(false)}
              >
                ×
              </button>
            </div>

            <div className="om-export-head">
              <div>
                <h2>{cityName || '—'}</h2>
                <p>Население 18+: {(cityPopulation / 1000).toFixed(0)} тыс. · 30 дней</p>
              </div>
              <div className="om-export-campaign">
                <b>{campaign?.name || 'Кампания'}</b>
                <span>
                  {formatRuDate(campaign?.startDate)} – {formatRuDate(campaign?.endDate)}
                </span>
              </div>
            </div>

            <section className="om-export-section">
              <h3>Общая информация</h3>
              <div className="om-export-grid">
                <div><span>GRP, %</span><b>{derivedKpi.grp || '—'}</b></div>
                <div><span>CPU, ₽</span><b>{derivedKpi.cpu || '—'}</b></div>
                <div><span>Поверхностей</span><b>{lenSelectedBanners || HandChooseBanners.length || 0}</b></div>
                <div><span>Стоимость, ₽</span><b>{fmtMoney(countMoney || stats.totalBudget)}</b></div>
                <div><span>CPT, ₽</span><b>{cptFinal || '—'}</b></div>
              </div>
            </section>

            <section className="om-export-section">
              <h3>Медиаметрические показатели</h3>
              <div className="om-export-grid">
                <div>
                  <span>
                    Охват, %
                  </span>
                  <b>{Number(coverCityPerc || stats.real_perc_cover || 0).toFixed(1)}</b>
                </div>
                <div><span>Охват, тыс.</span><b>{coverPeople}</b></div>
                <div><span>Reach 5+</span><b>{reachFivePlus.toFixed(1)}%</b></div>
                <div><span>Frequency</span><b>{Number(u_s || stats.frequency || 0).toFixed(2)}</b></div>
                <div><span>OTS, тыс.</span><b>{((Number(otsFinal) || 0) / 1000).toFixed(1)}</b></div>
              </div>
            </section>

            <section className="om-export-section">
              <h3>Охваты по частотам с накоплением</h3>
              <div className="om-export-chart">
                {!hasDaysChartData(dataFullDays) && (
                  <div className="om-chart-empty">Нет данных</div>
                )}
                <canvas id={OM_EXPORT_CHART_DAYS_ID} />
              </div>
            </section>

            <section className="om-export-section">
              <h3>% охвата с разной частотой</h3>
              <div className="om-export-chart">
                {!hasFreqChartData(dataKasaniya) && (
                  <div className="om-chart-empty">Нет данных</div>
                )}
                <canvas id={OM_EXPORT_CHART_FREQ_ID} />
              </div>
            </section>

            <section className="om-export-section om-export-section--surfaces">
              <h3>Выбранные поверхности</h3>
              <div className="om-export-surfaces">
                {[...HandChooseBanners]
                  .sort((a, b) => (b.unique_cover_perc || 0) - (a.unique_cover_perc || 0))
                  .map((item, index) => (
                    <div className="om-export-surface" key={`${item.id}-${index}`}>
                      <div>
                        <b>{item.id}</b>
                        <span>{item['Адрес'] || item.address || '—'}</span>
                      </div>
                      <div className="om-export-surface-foot">
                        <span className="plus">(+{item.unique_cover_perc || 0}%)</span>
                        <b>{fmtMoney(item.price)} ₽</b>
                      </div>
                    </div>
                  ))}
                {!HandChooseBanners.length && (
                  <p className="om-hint">Нет выбранных поверхностей.</p>
                )}
              </div>
            </section>
          </div>
        </div>
      )}


      <div
        className={[
          'om-body',
          goalCollapsed ? 'goal-collapsed' : '',
          analyticsCollapsed ? 'analytics-collapsed' : '',
        ].filter(Boolean).join(' ')}
      >
        <section className="om-map-wrap" data-tour="map">
          <div className="om-map-toolbar">
            <div className="om-map-mode-switch" role="group" aria-label="Тип карты">
              <button
                type="button"
                className={`om-map-mode-seg${activeMapType !== 'traffic' ? ' is-active' : ''}`}
                onClick={() => setMapType('default')}
                aria-pressed={activeMapType !== 'traffic'}
              >
                <span className="om-map-mode-ico" aria-hidden>
                  <IconMapBanner />
                </span>
                <span>Баннера</span>
              </button>
              <button
                type="button"
                className={`om-map-mode-seg${activeMapType === 'traffic' ? ' is-active' : ''}`}
                onClick={() => setMapType('traffic')}
                aria-pressed={activeMapType === 'traffic'}
              >
                <span className="om-map-mode-ico" aria-hidden>
                  <IconTraffic />
                </span>
                <span>Трафик</span>
              </button>
            </div>
            {activeMapType !== 'traffic' && (
              <div className="om-map-tags">
                {formatTags.map((tag) => (
                  <button
                    key={tag.key}
                    type="button"
                    className={`om-tag${selectedFormat === tag.key ? ' active' : ''}`}
                    onClick={() => setSelectedFormat(tag.key)}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              className="om-icon-btn om-map-fullscreen-btn"
              title={isMapFullscreen ? 'Выйти из полного экрана' : 'На весь экран'}
              aria-label={isMapFullscreen ? 'Выйти из полного экрана' : 'На весь экран'}
              onClick={() => {
                const mapWrap = document.querySelector('.om-map-wrap');
                if (!mapWrap) return;
                if (document.fullscreenElement) {
                  document.exitFullscreen?.()
                    .then(() => setIsMapFullscreen(false))
                    .catch(() => {});
                } else {
                  mapWrap.requestFullscreen?.()
                    .then(() => setIsMapFullscreen(true))
                    .catch(() => {});
                }
              }}
            >
              {isMapFullscreen ? <IconCompress /> : <IconExpand />}
            </button>
          </div>

          <div className="om-map-float mid">
            <button
              type="button"
              className="om-icon-btn"
              onClick={() => {
                const map = activeMapType === 'traffic' ? trafficMapRef.current : mapRef.current;
                map?.zoomIn();
              }}
              aria-label="Приблизить"
            >
              <IconPlus />
            </button>
            <button
              type="button"
              className="om-icon-btn"
              onClick={() => {
                const map = activeMapType === 'traffic' ? trafficMapRef.current : mapRef.current;
                map?.zoomOut();
              }}
              aria-label="Отдалить"
            >
              <IconMinus />
            </button>
          </div>

          <div className="om-map-float bottom">
            <button
              type="button"
              className={`om-icon-btn om-map-traffic-btn${activeMapType === 'traffic' ? ' is-active' : ''}`}
              title={activeMapType === 'traffic' ? 'Карта баннеров' : 'Карта трафика'}
              aria-label={activeMapType === 'traffic' ? 'Карта баннеров' : 'Карта трафика'}
              onClick={toggleMapType}
            >
              <IconTraffic />
            </button>
          </div>

          {(trafficMapWarm || activeMapType === 'traffic') && (
            <div
              className={`om-traffic-map${activeMapType === 'traffic' ? '' : ' is-hidden'}`}
              aria-hidden={activeMapType !== 'traffic'}
            >
              <TrafficMap
                bnr_zoom_coordinates={mapCenter}
                zoomLevel={zoomLevel}
                choosed_banner_id={choosedBanner}
                trafficMapPath={trafficMapPath}
                isVisible={activeMapType === 'traffic'}
                mapRef={trafficMapRef}
              />
            </div>
          )}

          {activeMapType !== 'traffic' && (
            <MapContainer
              className="om-map"
              center={mapCenter}
              zoom={zoomLevel}
              zoomControl={false}
            >
              <MapBridge mapRef={mapRef} />
              <ChangeView center={mapCenter} zoom={zoomLevel} />
              <TileLayer
                url={CARTO_LIGHT_TILE_URL}
                attribution={CARTO_TILE_ATTRIBUTION}
              />
              {mapMarkers.map((m) => (
                <Marker
                  key={String(m.id)}
                  position={m.position}
                  icon={m.deleted ? deletedIcon : m.selected ? selectedIcon : dotIcon}
                  eventHandlers={{
                    mouseover: (e) => {
                      const el = e.target.getElement?.();
                      if (el) el.classList.add('is-hover');
                    },
                    mouseout: (e) => {
                      const el = e.target.getElement?.();
                      if (el) el.classList.remove('is-hover');
                    },
                  }}
                >
                  <Popup closeButton={false} className="custom-map-popup om-map-popup">
                    <BannerPopup
                      id={m.id}
                      address={m.address}
                      photoLink={m.photo}
                      mapLink={m.map}
                      price={m.price != null && m.price !== '' ? `${fmtMoney(m.price)} ₽` : '—'}
                      action={createBannerAction(m.selected, m.id)}
                      actionLabel={m.deleted ? 'MarkerIconRedBanner' : m.selected ? 'MarkerIconBlueBanner' : 'MarkerIconDot'}
                      isDeleted={m.deleted}
                      onDelete={createBannerDelete(m.id)}
                      onRestore={createBannerRestore(m.id)}
                      trafficActive={activeMapType === 'traffic' && String(choosedBanner) === String(m.id)}
                      onShowTraffic={() => {
                        setChoosedBanner(m.id);
                        setMapCenter(m.position);
                        setZoomLevel(16);
                        setActiveMapType('traffic');
                      }}
                    />
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}

          <div className={`om-chart-card${isChartCollapsed ? ' is-collapsed' : ''}`}>
            <div className="om-chart-head">
              <div className="om-chart-tabs">
                <button
                  type="button"
                  className={choosedGraphicBottom === 'first_graphic' ? 'active' : 'not-active'}
                  onClick={() => {
                    setChoosedGraphicBottom('first_graphic');
                    if (isChartCollapsed) setIsChartCollapsed(false);
                  }}
                >
                  Охват/Частота
                </button>
                <button
                  type="button"
                  className={choosedGraphicBottom === 'sec_graphic' ? 'active' : 'not-active'}
                  onClick={() => {
                    setChoosedGraphicBottom('sec_graphic');
                    if (isChartCollapsed) setIsChartCollapsed(false);
                  }}
                >
                  Кривая насыщения
                </button>
              </div>
              <button
                type="button"
                className="om-collapse"
                onClick={() => setIsChartCollapsed((prev) => !prev)}
                aria-label={isChartCollapsed ? 'Развернуть график' : 'Свернуть график'}
                title={isChartCollapsed ? 'Развернуть график' : 'Свернуть график'}
              >
                <IconChevronDown open={isChartCollapsed} />
              </button>
            </div>
            {!isChartCollapsed && (
              <div className="om-chart-plot">
                {(choosedGraphicBottom === 'first_graphic'
                  ? !hasDaysChartData(dataFullDays)
                  : !hasFreqChartData(dataKasaniya)) && (
                  <div className="om-chart-empty">Нет данных</div>
                )}
                {choosedGraphicBottom === 'first_graphic' ? (
                  <canvas id={OM_CHART_DAYS_ID} key={`days-${isChartCollapsed}`} />
                ) : (
                  <canvas id={OM_CHART_FREQ_ID} key={`freq-${isChartCollapsed}`} />
                )}
              </div>
            )}
          </div>
        </section>

        <aside className={`om-panel om-panel--goals${goalCollapsed ? ' is-collapsed' : ''}`} data-tour="goals">
          {goalCollapsed ? (
            <button
              type="button"
              className="om-panel-rail"
              onClick={() => setGoalCollapsed(false)}
              aria-label="Развернуть панель цели"
              title="Развернуть панель цели"
            >
              <IconChevronRight />
              <span>Цель</span>
            </button>
          ) : (
            <>
          <div className="om-panel-head">
            <h2>Цель компании</h2>
            <button
              type="button"
              className="om-collapse"
              aria-label="Свернуть панель цели"
              title="Свернуть панель цели"
              onClick={() => setGoalCollapsed(true)}
            >
              <IconChevronLeft />
            </button>
          </div>

          <div className="om-panel-body">
            <div className="om-goal-list" data-tour="goal-options">
              {GOALS.map((goal, index) => (
                <button
                  key={goal}
                  type="button"
                  className={`om-goal${selectedGoal === index ? ' active' : ''}`}
                  onClick={() => setSelectedGoal(index)}
                >
                  <span className="om-radio" />
                  <span>{goal}</span>
                </button>
              ))}
            </div>

            {selectedGoal === 0 && (
              <div className="om-field" data-tour="calculate">
                <label>Общий бюджет (руб)</label>
                <div className="om-field-row">
                  <input
                    value={budgetInput}
                    onChange={(e) => setBudgetInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="1000000"
                    inputMode="numeric"
                  />
                  <button
                    type="button"
                    className="om-btn om-btn-primary"
                    disabled={isProcessing || metricsBlockCalculations}
                    onClick={() => processInput('cost')}
                  >
                    {isProcessing ? '…' : 'Рассчитать'}
                  </button>
                </div>
                <div className="om-option-toggles">
                  <label className="om-toggle-row">
                    <span>Минимизировать стоимость просмотра</span>
                    <span className="om-switch">
                      <input
                        type="checkbox"
                        checked={withMoney}
                        onChange={(e) => setWithMoney(e.target.checked)}
                      />
                      <span className="om-switch-ui" aria-hidden="true" />
                    </span>
                  </label>
                  <label className="om-toggle-row">
                    <span>Эффективный охват (Reach N+)</span>
                    <span className="om-switch">
                      <input
                        type="checkbox"
                        checked={useReachNPlus}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setUseReachNPlus(on);
                          if (on) setReachN('1');
                        }}
                      />
                      <span className="om-switch-ui" aria-hidden="true" />
                    </span>
                  </label>
                  {useReachNPlus && (
                    <div className="om-reach-n">
                      <div className="om-reach-n-head">
                        <label htmlFor="om-reach-n-input-0">Минимум касаний (N)</label>
                        <div className="om-reach-n-stepper">
                          <button
                            type="button"
                            className="om-reach-n-btn"
                            aria-label="Уменьшить N"
                            disabled={normalizeNumber(reachN, 1) <= 1}
                            onClick={() => setReachN(String(Math.max(1, normalizeNumber(reachN, 1) - 1)))}
                          >
                            −
                          </button>
                          <input
                            id="om-reach-n-input-0"
                            className="om-reach-n-input"
                            value={reachN}
                            onChange={(e) => {
                              let v = e.target.value.replace(/\D/g, '');
                              if (Number(v) > REACH_N_UI_MAX) v = String(REACH_N_UI_MAX);
                              setReachN(v);
                            }}
                            onBlur={() => {
                              if (!reachN || normalizeNumber(reachN, 0) < 1) setReachN('1');
                              else if (normalizeNumber(reachN, 1) > REACH_N_UI_MAX) {
                                setReachN(String(REACH_N_UI_MAX));
                              }
                            }}
                            placeholder="1"
                            inputMode="numeric"
                          />
                          <button
                            type="button"
                            className="om-reach-n-btn"
                            aria-label="Увеличить N"
                            disabled={normalizeNumber(reachN, 1) >= REACH_N_UI_MAX}
                            onClick={() => setReachN(String(Math.min(REACH_N_UI_MAX, normalizeNumber(reachN, 1) + 1)))}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <p className="om-hint">Считать человека охваченным только при ≥ N контактах (1–{REACH_N_UI_MAX}).</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedGoal === 1 && (
              <div className="om-field" data-tour="calculate">
                <label>Доля охвата (%)</label>
                <div className="om-field-row">
                  <input
                    value={percentage}
                    onChange={(e) => {
                      let v = e.target.value.replace(/\D/g, '');
                      if (Number(v) > 100) v = '100';
                      setPercentage(v);
                    }}
                    placeholder="50"
                    inputMode="numeric"
                  />
                  <button
                    type="button"
                    className="om-btn om-btn-primary"
                    disabled={isProcessing || metricsBlockCalculations}
                    onClick={() => processInput('perc')}
                  >
                    {isProcessing ? '…' : 'Рассчитать'}
                  </button>
                </div>
                <div className="om-option-toggles">
                  <label className="om-toggle-row">
                    <span>Минимизировать стоимость просмотра</span>
                    <span className="om-switch">
                      <input
                        type="checkbox"
                        checked={withMoney}
                        onChange={(e) => setWithMoney(e.target.checked)}
                      />
                      <span className="om-switch-ui" aria-hidden="true" />
                    </span>
                  </label>
                  <label className="om-toggle-row">
                    <span>Эффективный охват (Reach N+)</span>
                    <span className="om-switch">
                      <input
                        type="checkbox"
                        checked={useReachNPlus}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setUseReachNPlus(on);
                          if (on) setReachN('1');
                        }}
                      />
                      <span className="om-switch-ui" aria-hidden="true" />
                    </span>
                  </label>
                  {useReachNPlus && (
                    <div className="om-reach-n">
                      <div className="om-reach-n-head">
                        <label htmlFor="om-reach-n-input-1">Минимум касаний (N)</label>
                        <div className="om-reach-n-stepper">
                          <button
                            type="button"
                            className="om-reach-n-btn"
                            aria-label="Уменьшить N"
                            disabled={normalizeNumber(reachN, 1) <= 1}
                            onClick={() => setReachN(String(Math.max(1, normalizeNumber(reachN, 1) - 1)))}
                          >
                            −
                          </button>
                          <input
                            id="om-reach-n-input-1"
                            className="om-reach-n-input"
                            value={reachN}
                            onChange={(e) => {
                              let v = e.target.value.replace(/\D/g, '');
                              if (Number(v) > REACH_N_UI_MAX) v = String(REACH_N_UI_MAX);
                              setReachN(v);
                            }}
                            onBlur={() => {
                              if (!reachN || normalizeNumber(reachN, 0) < 1) setReachN('1');
                              else if (normalizeNumber(reachN, 1) > REACH_N_UI_MAX) {
                                setReachN(String(REACH_N_UI_MAX));
                              }
                            }}
                            placeholder="1"
                            inputMode="numeric"
                          />
                          <button
                            type="button"
                            className="om-reach-n-btn"
                            aria-label="Увеличить N"
                            disabled={normalizeNumber(reachN, 1) >= REACH_N_UI_MAX}
                            onClick={() => setReachN(String(Math.min(REACH_N_UI_MAX, normalizeNumber(reachN, 1) + 1)))}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <p className="om-hint">Считать человека охваченным только при ≥ N контактах (1–{REACH_N_UI_MAX}).</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* —— inventory upload —— */}
            <section className="om-inventory" data-tour="inventory">
              <div className="om-inventory-head">
                <h3>Загрузка инвентаря</h3>
                {sampleCsv && (
                  <a
                    className="om-inventory-sample"
                    href={sampleCsv.href}
                    download={sampleCsv.download}
                    data-tour="inventory-sample"
                  >
                    Пример CSV
                  </a>
                )}
              </div>
              <p className="om-hint">Поддерживаются форматы с ресурсов Russ / all-billboards / Media Catalog</p>

              {(uploading || metricsStatus === 'processing') ? (
                <div className="om-inventory-loading" role="status">
                  <div className="om-inventory-spinner" />
                  <div>
                    <b>Загружаем и считаем метрики</b>
                    <p>{metricsPhaseLabel}</p>
                    <p className="om-hint">Не закрывайте вкладку</p>
                  </div>
                </div>
              ) : (
                <div
                  className={`om-dropzone${dragActive ? ' is-drag' : ''}${inventoryOriginalFilename ? ' has-file' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.pdf"
                    hidden
                    onChange={handleFileSelect}
                  />
                  <div className="om-dropzone-icon">
                    <IconUpload />
                  </div>
                  <div className="om-dropzone-title">Перетащите CSV сюда (или другой поддерживаемый формат)</div>
                  <div className="om-dropzone-sub">нажмите, чтобы выбрать файл</div>
                  <button
                    type="button"
                    className="om-btn om-btn-primary om-dropzone-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    Загрузить инвентарь
                  </button>
                </div>
              )}

              {uploadError && (
                <div className="om-inventory-banner om-inventory-banner--error">{uploadError}</div>
              )}
              {metricsStatus === 'failed' && !uploadError && (
                <div className="om-inventory-banner om-inventory-banner--error">
                  Не удалось посчитать метрики. Загрузите файл ещё раз.
                </div>
              )}
              {inventoryOriginalFilename && metricsStatus !== 'processing' && (
                <div className="om-inventory-current">
                  <IconFile />
                  <div>
                    <span>Текущий файл</span>
                    <b>{inventoryOriginalFilename}</b>
                  </div>
                </div>
              )}
              {uploadedBnrs > 0 && metricsStatus === 'ready' && (
                <div className="om-inventory-banner om-inventory-banner--ok">
                  Успешно загружено {uploadedBnrs} {pluralBanners(uploadedBnrs)}
                </div>
              )}
              {NotTouchesBnrs > 0 && metricsStatus === 'ready' && (
                <div className="om-inventory-banner om-inventory-banner--warn">
                  Без касаний в симуляции: {NotTouchesBnrs} {pluralBanners(NotTouchesBnrs)}
                </div>
              )}
            </section>
          </div>
            </>
          )}
        </aside>

        <aside className={`om-panel om-panel--analytics${analyticsCollapsed ? ' is-collapsed' : ''}`} data-tour="analytics">
          {analyticsCollapsed ? (
            <button
              type="button"
              className="om-panel-rail"
              onClick={() => setAnalyticsCollapsed(false)}
              aria-label="Развернуть аналитику"
              title="Развернуть аналитику"
            >
              <IconChevronLeft />
              <span>Аналитика</span>
            </button>
          ) : (
            <>
          <div className="om-panel-head">
            <h2>Сводная аналитика</h2>
            <button
              type="button"
              className="om-collapse"
              aria-label="Свернуть аналитику"
              title="Свернуть аналитику"
              onClick={() => setAnalyticsCollapsed(true)}
            >
              <IconChevronRight />
            </button>
          </div>
          <div className="om-panel-body">
            <div className="om-metrics">
              <div className="om-metric"><span>Всего поверхностей</span><b>{lenSelectedBanners || HandChooseBanners.length || 0}</b></div>
              <div className="om-metric">
                <span>
                  Охват
                </span>
                <b>
                  {coverPeople} тыс. чел ({Number(coverCityPerc || stats.real_perc_cover || 0).toFixed(1)}%)
                </b>
              </div>
              <div className="om-metric"><span>Reach 5+</span><b>{reachFivePlus.toFixed(1)}%</b></div>
              <div className="om-metric">
                <span>OTS</span>
                <b>{fmtMoney(otsFinal)}</b>
              </div>
              <div className="om-metric">
                <span>Frequency</span>
                <b>{Number(u_s || stats.frequency || 0).toFixed(2)}</b>
              </div>
              <div className="om-metric"><span>Стоимость (₽)</span><b>{fmtMoney(countMoney || stats.totalBudget)}</b></div>
              <div className="om-metric"><span>CPT (₽)</span><b>{cptFinal || '—'}</b></div>
              <div className="om-metric"><span>CPU (₽)</span><b>{derivedKpi.cpu || '—'}</b></div>
              <div className="om-metric"><span>GRP (%)</span><b>{derivedKpi.grp || '—'}</b></div>
            </div>

            {showSurfaces && (
              <div className="om-surfaces">
                {[...HandChooseBanners]
                  .sort((a, b) => (b.unique_cover_perc || 0) - (a.unique_cover_perc || 0))
                  .map((item, index) => (
                    <div
                      className="om-surface"
                      key={`${item.id}-${index}`}
                      onClick={() => {
                        const pos = parseCoords(item) || (item.coordinates ? item.coordinates.split(',').map(Number) : null);
                        if (pos) {
                          setMapCenter(pos);
                          setZoomLevel(16);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="om-surface-title">
                        {item.id} | {item['Адрес'] || item.address || '—'}
                      </div>
                      <div className="om-surface-foot">
                        <span className="plus">(+{item.unique_cover_perc || 0}%)</span>
                        <b>{fmtMoney(item.price)} ₽</b>
                      </div>
                    </div>
                  ))}
                {!HandChooseBanners.length && (
                  <p className="om-hint">Пока нет выбранных поверхностей — загрузите инвентарь и нажмите «Рассчитать».</p>
                )}
              </div>
            )}
          </div>
          <div className="om-panel-foot">
            <button type="button" className="om-btn" onClick={() => setShowSurfaces((v) => !v)}>
              <IconEye />
              {showSurfaces ? 'Скрыть список поверхностей' : 'Показать список поверхностей'}
            </button>
          </div>
          {(isProcessing || metricsBlockCalculations || uploading) && (
            <div className="om-panel-busy" role="status" aria-label="Считаем метрики">
              <img src={loadingCapybaraGif} alt="" className="om-panel-busy-gif" />
            </div>
          )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

const App2OptiMix = withCampaignAccess(App2OptiMixScreen);
export default App2OptiMix;
